import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Calculator, FolderPlus, Sparkles, Loader2, Table, Grid3x3,
  ListTree, FileSpreadsheet, ShieldCheck, Trash2, RefreshCw, Save, Home, Sigma, TrendingUp,
  Wallet, Percent, LineChart, HandCoins, Layers, FileStack, FileBarChart, Settings, Users,
  Menu, Download, Printer, Undo2, Redo2, Copy, Scissors, ClipboardPaste, PanelLeft, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/* ── Paleta definitiva (§16): claro/elegante, navy petróleo + verde COREX ── */
const NAVY = "#072F43";
const INK = "#17263A";
const BG = "#F7F9FC";
const GREEN = "#0c8a5a";

const money = (n) =>
  `S/ ${Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const n2 = (n) => Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct = (n) => `${(Number(n || 0) * 100).toFixed(2)}%`;

/* Clases reutilizables (paleta clara) */
const CARD = "rounded-lg border border-slate-200 bg-white shadow-sm";
const THEAD = "bg-[#0b3a4d] text-white";
const btnGhost = "inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[12px] font-medium text-slate-600 hover:bg-slate-50 hover:text-[#17263A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed";
const secTitle = "text-[11px] font-bold uppercase tracking-wide text-[#0b3a4d]";

/* Cinta lateral jerárquica: grupos desplegables + reubicación (solo navegación) */
const NAV_TREE = [
  { key: "inicio", label: "Inicio", icon: Home },
  { key: "g-presupuesto", label: "Presupuesto", icon: ListTree, children: [
    { key: "presupuesto", label: "Presupuesto Contractual" },
    { key: "adendas", label: "Adendas / Versiones" },
  ] },
  { key: "g-valorizaciones", label: "Valorizaciones", icon: Grid3x3, children: [
    { key: "valorizacion", label: "Valorización Actual" },
    { key: "metrados", label: "Metrados por Valorización" },
  ] },
  { key: "g-reajuste", label: "Cálculos de Reajuste", icon: TrendingUp, children: [
    { key: "indices", label: "Índices Unificados" },
    { key: "reajuste", label: "Cálculo de Reajuste" },
    { key: "reintegro", label: "Cálculo de Reintegro" },
    { key: "amortizacion", label: "Amortización A.D." },
    { key: "deduccion", label: "Deducción A.D." },
  ] },
  { key: "g-control", label: "Control de Ejecución", icon: LineChart, children: [
    { key: "control", label: "Porcentaje de Ejecución" },
    { key: "control-financiero", label: "Financiero Programado" },
  ] },
  { key: "g-herramientas", label: "Herramientas", icon: FileBarChart, children: [
    { key: "auditoria", label: "Auditoría" },
    { key: "reportes", label: "Reportes" },
  ] },
];
const LABELS = { inicio: "Inicio" };
NAV_TREE.forEach((n) => { if (n.children) n.children.forEach((c) => (LABELS[c.key] = c.label)); });
const labelOf = (k) => LABELS[k] || "";
const groupOfMod = (m) => (NAV_TREE.find((n) => n.children && n.children.some((c) => c.key === m)) || {}).key;
const PROC_MODS = ["inicio", "reajuste", "reintegro", "amortizacion", "deduccion", "valorizacion"];
const CONTROL_MODS = ["control", "control-financiero"];

/* ── Fila resumen (Pantalla 02) ─────────────────────────────────────────── */
const AuxRow = ({ label, value, strong, tone }) => (
  <div className={`flex items-center justify-between text-[12px] ${strong ? "border-t border-slate-200 pt-1.5 font-bold text-[#17263A]" : "text-slate-500"}`}>
    <span>{label}</span>
    <span className={`tabular-nums ${tone === "green" ? "text-emerald-700 font-semibold" : tone === "red" ? "text-red-600" : tone === "amber" ? "text-amber-600" : ""}`}>{value}</span>
  </div>
);

/* ── Panel resumen económico (§75, contextual de Valorización) ──────────── */
const ResumenEconomico = ({ vlz }) => {
  if (!vlz) return <div className="text-[12px] text-slate-400">Sin cálculo aún.</div>;
  const e = vlz.economic || {};
  const rows = [
    ["Costo de obra valorizado", money(e.costo_obra), false],
    ["Factor K aplicado", vlz.k || "—", false],
    ["(+) Reajuste", money(e.reajuste), false],
    ["(+) Reintegro", money(e.reintegro), false],
    ["= Valorización Bruta", money(e.valorizacion_bruta), true],
    ["(−) Deducción", money(e.deduccion), false],
    ["(−) Amortización A.D.", money(e.amortizacion), false],
    ["= Valorización Neta", money(e.valorizacion_neta), true],
    ["(+) IGV", money(e.igv), false],
  ];
  return (
    <div data-testid="vlz-resumen-economico">
      <div className="space-y-1.5">
        {rows.map(([l, v, s]) => <AuxRow key={l} label={l} value={v} strong={s} />)}
      </div>
      <div className="mt-3 rounded-md border border-emerald-500/50 bg-emerald-50 p-2.5 flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase text-emerald-700">Monto a Cancelar</span>
        <span className="text-[15px] font-extrabold text-emerald-700 tabular-nums" data-testid="vlz-monto-cancelar">
          {money(e.monto_a_cancelar)}
        </span>
      </div>
      {vlz.any_sobrevalorizacion && (
        <div className="mt-2 text-[11px] font-semibold text-red-600">● Existe sobrevalorización en alguna partida (§27)</div>
      )}
    </div>
  );
};

export default function CorexValorizaciones() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [role, setRole] = useState("USUARIO");
  const [active, setActive] = useState(null);
  const [period, setPeriod] = useState(1);
  const [vlz, setVlz] = useState(null);
  const [mod, setMod] = useState("valorizacion");  // módulo del sidebar
  const [collapsed, setCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState({ "g-valorizaciones": true });
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [edits, setEdits] = useState({});
  const [controlData, setControlData] = useState(null);
  const [audit, setAudit] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [iu, setIu] = useState({ regime: "1992", area: "3", items: [], kResult: null });
  const [adenda, setAdenda] = useState({ text: "", rows: null, from: 2 });
  const [prog, setProg] = useState({});
  const [sel, setSel] = useState({ anchor: null, focus: null });
  const [modoTecnico, setModoTecnico] = useState(false);
  const [modoTecnicoCtl, setModoTecnicoCtl] = useState({ puede_alternar: false, puede_ver: false });
  const [areasInfo, setAreasInfo] = useState(null);
  const [pendingIU, setPendingIU] = useState(null);
  const [ingestaBusy, setIngestaBusy] = useState(false);
  const [vScrollM, setVScrollM] = useState(0);
  const [vScrollV, setVScrollV] = useState(0);
  const [search, setSearch] = useState("");
  const [proc, setProc] = useState(null);
  const [procRow, setProcRow] = useState(null);
  const [showDetalle, setShowDetalle] = useState(false);
  const [pie, setPie] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [budgetEdits, setBudgetEdits] = useState({});
  const [importPreview, setImportPreview] = useState(null);
  const fileInputRef = useRef(null);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/valorizaciones/projects");
      setProjects(data.projects || []);
      setRole(data.role || "USUARIO");
    } catch (e) { toast.error("No se pudieron cargar los proyectos"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadProjects(); }, [loadProjects]);

  const periodsOf = (doc) => {
    const ks = Object.keys(doc?.metrados || {}).map(Number);
    return ks.length ? ks.sort((a, b) => a - b) : [1];
  };

  const openProject = useCallback(async (pid) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/valorizaciones/projects/${pid}`);
      const doc = data.project;
      setActive(doc);
      const ps = periodsOf(doc);
      const p = ps[ps.length - 1];
      setPeriod(p); setEdits({}); setMod("valorizacion");
      const r = await api.get(`/valorizaciones/projects/${pid}/valorizacion/${p}`);
      setVlz(r.data);
      try {
        const mt = await api.get(`/valorizaciones/projects/${pid}/modo-tecnico`);
        setModoTecnico(!!mt.data.enabled);
        setModoTecnicoCtl({ puede_alternar: !!mt.data.puede_alternar, puede_ver: !!mt.data.puede_ver });
      } catch { setModoTecnicoCtl({ puede_alternar: false, puede_ver: false }); }
    } catch (e) { toast.error("No se pudo abrir el proyecto"); }
    finally { setBusy(false); }
  }, []);

  const recompute = useCallback(async (pid, p) => {
    const r = await api.get(`/valorizaciones/projects/${pid}/valorizacion/${p}`);
    setVlz(r.data);
  }, []);
  const loadProc = async (pid, p) => {
    try { const { data } = await api.get(`/valorizaciones/projects/${pid}/procedimiento/${p}`); setProc(data); }
    catch { setProc(null); }
  };

  const changePeriod = async (p) => {
    setPeriod(p); setEdits({});
    if (active) { await recompute(active.projectId, p); await loadProc(active.projectId, p); }
  };

  const calcular = async () => {
    if (!active) return;
    setBusy(true);
    try {
      await recompute(active.projectId, period);
      await loadProc(active.projectId, period);
      toast.success("Recalculado con el motor certificado (S/ 0.00 de tolerancia)");
    } catch { toast.error("No se pudo recalcular"); }
    finally { setBusy(false); }
  };

  const exportFile = async (kind) => {
    if (!active) return;
    const base = process.env.REACT_APP_BACKEND_URL;
    const tok = localStorage.getItem("cl_access_token");
    const url = kind === "pdf"
      ? `${base}/api/valorizaciones/projects/${active.projectId}/export/pdf?period=${period}`
      : `${base}/api/valorizaciones/projects/${active.projectId}/export/excel?period=${period}&kind=${kind}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = kind === "pdf" ? `Valorizacion_${period}.pdf` : `Valorizacion_${period}_${kind}.xlsx`;
      a.click();
      toast.success(`Exportado (${kind})`);
    } catch { toast.error("No se pudo exportar"); }
  };

  const toggleMode = async () => {
    if (!active || role === "AUDITOR") return;
    const next = active.mode === "directa" ? "con_reajustes" : "directa";
    try {
      await api.put(`/valorizaciones/projects/${active.projectId}/mode`, { mode: next, confirm: true });
      const { data } = await api.get(`/valorizaciones/projects/${active.projectId}`);
      setActive(data.project); await recompute(active.projectId, period);
      toast.success(`Modo: ${next === "directa" ? "DIRECTA" : "CON REAJUSTES"}`);
    } catch { toast.error("No se pudo cambiar el modo"); }
  };

  const importPaste = async () => {
    if (!active) return;
    const tsv = window.prompt("Pega el presupuesto desde Excel (Item, Descripción, Und., Metrado, Precio, Parcial):");
    if (!tsv) return;
    try {
      const { data } = await api.post("/valorizaciones/import/budget/paste", { tsv });
      const ok = window.confirm(`Reconocidas ${data.partidas.length} filas · válido: ${data.validation.ok}. ¿Confirmar e importar al presupuesto?`);
      if (!ok) return;
      await api.post(`/valorizaciones/projects/${active.projectId}/budget`, { partidas: data.partidas, confirmImpact: true });
      const { data: full } = await api.get(`/valorizaciones/projects/${active.projectId}`);
      setActive(full.project); await recompute(active.projectId, period);
      toast.success("Presupuesto importado (pegado)");
    } catch { toast.error("No se pudo importar el pegado"); }
  };

  // §1–§6 · IMPORTAR PRESUPUESTO = ARCHIVO (Excel/PDF) con previsualización
  const openFileImport = () => {
    if (!active) return;
    if (!budgetEditable) { toast.error("V1 protegida (con valorizaciones). Para modificar el contractual usa Adendas / Versiones."); return; }
    fileInputRef.current?.click();
  };
  const onFileChosen = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !active) return;
    const name = f.name.toLowerCase();
    const isPdf = name.endsWith(".pdf");
    const isExcel = name.endsWith(".xlsx") || name.endsWith(".xls");
    if (!isPdf && !isExcel) { toast.error("Formato no soportado. Usa Excel (.xlsx/.xls) o PDF."); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const url = isPdf ? "/valorizaciones/import/budget/pdf" : "/valorizaciones/import/budget/excel";
      const { data } = await api.post(url, fd);
      setImportPreview({ ...data, source: isPdf ? "PDF" : "Excel", filename: f.name });
    } catch { toast.error("No se pudo leer el archivo. Verifica el formato y su estructura."); }
    finally { setBusy(false); }
  };
  const confirmFileImport = async () => {
    if (!importPreview || !active) return;
    setBusy(true);
    try {
      await api.post(`/valorizaciones/projects/${active.projectId}/budget`, { partidas: importPreview.partidas, confirmImpact: true });
      const { data } = await api.get(`/valorizaciones/projects/${active.projectId}`);
      setActive(data.project); await recompute(active.projectId, period);
      try { const { data: p } = await api.get(`/valorizaciones/projects/${active.projectId}/presupuesto-pie`); setPie(p); } catch { /* pie opcional */ }
      const n = importPreview.partidas.length;
      setImportPreview(null);
      toast.success(`Presupuesto importado (${n} filas) e incorporado al contractual`);
    } catch (e) { toast.error(e?.response?.status === 409 ? "V1 protegida: usa Adendas / Versiones" : "No se pudo confirmar la importación"); }
    finally { setBusy(false); }
  };

  // §11/§26 · Edición multicelda rectangular con protección histórica
  const pushUndo = (snapshot) => { setUndoStack((s) => [...s, snapshot]); setRedoStack([]); };
  const isClosed = useCallback((p) => (active?.status_periods?.[String(p)] === "CERRADA"), [active]);
  const cellVal = useCallback((p, lid) => {
    if (edits[p] && edits[p][lid] !== undefined) return edits[p][lid];
    return (active?.metrados?.[String(p)] || {})[lid] || "";
  }, [edits, active]);
  const setCell = (p, lid, val) => setEdits((s) => ({ ...s, [p]: { ...(s[p] || {}), [lid]: val } }));
  const undo = () => {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    setRedoStack((r) => [...r, edits]); setEdits(prev); setUndoStack((s) => s.slice(0, -1));
  };
  const redo = () => {
    if (redoStack.length === 0) return;
    const nxt = redoStack[redoStack.length - 1];
    setUndoStack((s) => [...s, edits]); setEdits(nxt); setRedoStack((r) => r.slice(0, -1));
  };
  const selRect = () => {
    if (!sel.anchor || !sel.focus) return null;
    const r0 = Math.min(sel.anchor.r, sel.focus.r), r1 = Math.max(sel.anchor.r, sel.focus.r);
    const c0 = Math.min(sel.anchor.c, sel.focus.c), c1 = Math.max(sel.anchor.c, sel.focus.c);
    return { r0, r1, c0, c1 };
  };
  const inSelRect = (ri, ci) => { const R = selRect(); return R ? ri >= R.r0 && ri <= R.r1 && ci >= R.c0 && ci <= R.c1 : false; };
  const copySelection = async () => {
    const R = selRect(); if (!R) return;
    const lines = [];
    for (let ri = R.r0; ri <= R.r1; ri++) {
      const row = [];
      for (let ci = R.c0; ci <= R.c1; ci++) row.push(cellVal(allPeriods[ci], gridRows[ri].line_id));
      lines.push(row.join("\t"));
    }
    try { await navigator.clipboard.writeText(lines.join("\n")); } catch { /* clipboard bloqueado */ }
    const cells = (R.r1 - R.r0 + 1) * (R.c1 - R.c0 + 1);
    toast.success(`Copiadas ${cells} celda(s) (${R.r1 - R.r0 + 1}×${R.c1 - R.c0 + 1})`);
  };
  const cutSelection = async () => {
    if (role === "AUDITOR") return;
    const R = selRect(); if (!R) return;
    await copySelection(); pushUndo(edits);
    let skipped = 0;
    setEdits((s) => {
      const n = { ...s };
      for (let ri = R.r0; ri <= R.r1; ri++)
        for (let ci = R.c0; ci <= R.c1; ci++) {
          const p = allPeriods[ci];
          if (isClosed(p)) { skipped++; continue; }
          n[p] = { ...(n[p] || {}), [gridRows[ri].line_id]: "" };
        }
      return n;
    });
    toast.success(`Cortadas celdas${skipped ? ` · ${skipped} protegidas (periodo cerrado)` : ""}`);
  };
  const pasteBlock = (tsv) => {
    if (role === "AUDITOR") return;
    const matrix = tsv.replace(/\r/g, "").split("\n").filter((l) => l !== "").map((l) => l.split("\t"));
    if (matrix.length === 0) return;
    const R = selRect();
    const startR = R ? R.r0 : 0;
    const startC = R ? R.c0 : allPeriods.indexOf(period);
    pushUndo(edits);
    let written = 0, skipped = 0;
    setEdits((s) => {
      const n = { ...s };
      matrix.forEach((rowVals, i) => {
        rowVals.forEach((val, j) => {
          const ri = startR + i, ci = startC + j;
          if (ri >= gridRows.length || ci >= allPeriods.length || ci < 0) return;
          const p = allPeriods[ci];
          if (isClosed(p)) { skipped++; return; }
          n[p] = { ...(n[p] || {}), [gridRows[ri].line_id]: val.trim() }; written++;
        });
      });
      return n;
    });
    toast.success(`Pegadas ${written} celda(s) (${matrix.length}×${matrix[0].length})${skipped ? ` · ${skipped} protegidas` : ""} — confirma con Guardar`);
  };
  const focusCell = (ri, ci) => {
    if (ri < 0 || ri >= gridRows.length) return;
    const p = allPeriods[ci];
    if (p === undefined || isClosed(p)) return;
    const el = document.querySelector(`[data-testid='vlz-metrado-${p}-${gridRows[ri].line_id}']`);
    if (el) { el.focus(); if (el.select) el.select(); setSel({ anchor: { r: ri, c: ci }, focus: { r: ri, c: ci } }); }
  };

  const toggleModoTecnico = async () => {
    if (!active || !modoTecnicoCtl.puede_alternar) return;
    const next = !modoTecnico;
    try {
      await api.put(`/valorizaciones/projects/${active.projectId}/modo-tecnico`, { enabled: next });
      setModoTecnico(next); await recompute(active.projectId, period);
      toast.success(`Modo Técnico ${next ? "ACTIVADO" : "DESACTIVADO"}`);
    } catch (e) { toast.error(e?.response?.status === 403 ? "Solo ADMINISTRADOR" : "No se pudo cambiar el Modo Técnico"); }
  };

  const loadAreas = async (regime) => {
    try { const { data } = await api.get(`/valorizaciones/indices/areas?regime=${regime}`); setAreasInfo(data); }
    catch { setAreasInfo(null); }
  };
  const runIngesta = async () => {
    setIngestaBusy(true);
    try {
      const { data } = await api.post("/valorizaciones/indices/ingesta/run", { regime: iu.regime });
      toast.success(`Sincronización ejecutada · fuentes ${data.sources_checked.length} · errores ${data.errors.length}. Toda detección entra PENDIENTE.`);
      loadPending();
    } catch (e) { toast.error(e?.response?.status === 403 ? "Solo ADMINISTRADOR" : "No se pudo sincronizar"); }
    finally { setIngestaBusy(false); }
  };
  const loadPending = async () => {
    try { const { data } = await api.get(`/valorizaciones/indices/pending?regime=${iu.regime}`); setPendingIU(data); }
    catch { setPendingIU(null); }
  };
  const computeK = async () => {
    const raw = window.prompt("Índices actuales (código:valor separados por coma). Ej Junio 2020:\n47:619.99,05:437.63,03:554.21,21:449.56,43:955.06,30:518.60,39:464.67");
    if (!raw) return;
    const current = {};
    raw.split(",").forEach((p) => { const [c, v] = p.split(":"); if (c && v) current[c.trim()] = v.trim(); });
    try {
      const { data } = await api.post("/valorizaciones/indices/compute-k", { current });
      setIu((s) => ({ ...s, kResult: data })); toast.success(`K = ${data.K}`);
    } catch { toast.error("No se pudo calcular K"); }
  };
  const loadIU = async () => {
    try { const { data } = await api.get(`/valorizaciones/indices?regime=${iu.regime}&area=${iu.area}&status=all`); setIu((s) => ({ ...s, items: data.items })); }
    catch { toast.error("No se pudieron cargar los índices"); }
  };
  const addIU = async () => {
    const raw = window.prompt("Nuevo IU  →  codigo,descripcion,año,mes,valor_oficial,resolucion\nEj: 47,Mano de obra,2020,6,619.99,R.J. 140-2020-INEI");
    if (!raw) return;
    const [code, description, year, month, valor_oficial, resolucion] = raw.split(",").map((x) => x.trim());
    try {
      await api.post("/valorizaciones/indices", { regime: iu.regime, area: iu.area, code, description, year, month, valor_oficial, resolucion, status: "PENDIENTE", motivo: "Alta manual" });
      toast.success(`IU ${code} creado (PENDIENTE de revisión)`); loadIU();
    } catch (e) { toast.error(e?.response?.status === 403 ? "Solo ADMINISTRADOR" : "No se pudo crear el IU"); }
  };
  const approvePending = async () => {
    const pend = iu.items.filter((it) => it.status !== "OFICIAL").map((it) => ({ regime: it.regime, area: it.area, code: it.code, year: it.year, month: it.month }));
    if (pend.length === 0) { toast.info("No hay índices PENDIENTE"); return; }
    try { const { data } = await api.post("/valorizaciones/indices/approve", { keys: pend }); toast.success(`Aprobados ${data.approved} índice(s) → OFICIAL`); loadIU(); }
    catch (e) { toast.error(e?.response?.status === 403 ? "Solo ADMINISTRADOR" : "No se pudo aprobar"); }
  };
  const updateIU = () => toast.info("Flujo oficial: detección → PENDIENTE DE REVISIÓN → aprobación. Registra los IU publicados (INEI/El Peruano) con 'Agregar IU' y luego 'Aprobar pendientes'.");

  const adendaCompare = async () => {
    if (!adenda.text.trim()) { toast.info("Pega la estructura V2"); return; }
    const { data } = await api.post("/valorizaciones/import/budget/paste", { tsv: adenda.text });
    const cmp = await api.post(`/valorizaciones/projects/${active.projectId}/adenda/compare`, { partidas: data.partidas });
    setAdenda((s) => ({ ...s, rows: cmp.data.rows, blocked: cmp.data.bloqueado, partidas: data.partidas }));
  };
  const adendaApply = async () => {
    try {
      const { data } = await api.post(`/valorizaciones/projects/${active.projectId}/adenda/apply`, { vigencia_desde_periodo: adenda.from, partidas: adenda.partidas });
      toast.success(`Adenda aplicada: ${data.version} (desde Val.${data.from_period})`);
      const { data: full } = await api.get(`/valorizaciones/projects/${active.projectId}`); setActive(full.project);
    } catch (e) { toast.error(e?.response?.status === 409 ? "Inconsistencia contractual (nuevo < ejecutado)" : "No se pudo aplicar la adenda"); }
  };
  const saveProg = async () => {
    try {
      await api.put(`/valorizaciones/projects/${active.projectId}/programacion`, { programado: prog });
      const { data } = await api.get(`/valorizaciones/projects/${active.projectId}/control-avance`); setControlData(data);
      toast.success("Programación guardada");
    } catch { toast.error("No se pudo guardar la programación"); }
  };

  // Carga contextual por módulo
  useEffect(() => {
    if (!active) return;
    if (CONTROL_MODS.includes(mod)) api.get(`/valorizaciones/projects/${active.projectId}/control-avance`).then(({ data }) => setControlData(data)).catch(() => setControlData(null));
    if (mod === "auditoria") api.get(`/valorizaciones/projects/${active.projectId}/audit`).then(({ data }) => setAudit(data.events || [])).catch(() => setAudit([]));
    if (PROC_MODS.includes(mod)) loadProc(active.projectId, period);
    if (mod === "indices") { loadIU(); loadAreas(iu.regime); if (modoTecnicoCtl.puede_ver) loadPending(); }
    if (mod === "presupuesto") api.get(`/valorizaciones/projects/${active.projectId}/presupuesto-pie`).then(({ data }) => setPie(data)).catch(() => setPie(null));
  }, [mod, period, active, iu.regime, iu.area]); // eslint-disable-line

  // Abrir automáticamente el grupo del módulo activo
  useEffect(() => {
    const g = groupOfMod(mod);
    if (g) setOpenGroups((s) => (s[g] ? s : { ...s, [g]: true }));
  }, [mod]);

  const seedGolden = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/valorizaciones/projects/seed-golden");
      toast.success(data.golden.PASS ? `Golden verificado: ${money(data.golden.monto_a_cancelar)} ✅` : "Golden sembrado (revisar cifras)");
      await loadProjects(); await openProject(data.projectId);
    } catch (e) { toast.error("No se pudo sembrar el expediente Golden"); }
    finally { setBusy(false); }
  };
  const createProject = async () => {
    const name = window.prompt("Nombre del proyecto de valorización:");
    if (!name) return;
    setBusy(true);
    try { const { data } = await api.post("/valorizaciones/projects", { name }); await loadProjects(); await openProject(data.projectId); }
    catch (e) { toast.error("No se pudo crear el proyecto"); }
    finally { setBusy(false); }
  };
  const deleteProject = async (pid) => {
    if (!window.confirm("¿Eliminar este proyecto de valorización?")) return;
    try { await api.delete(`/valorizaciones/projects/${pid}`); if (active?.projectId === pid) { setActive(null); setVlz(null); } await loadProjects(); }
    catch (e) { toast.error("No se pudo eliminar"); }
  };
  const saveMetrados = async () => {
    if (!active) return;
    const editedPeriods = Object.keys(edits).filter((p) => Object.keys(edits[p] || {}).length > 0);
    if (editedPeriods.length === 0) { toast.info("Sin cambios que guardar"); return; }
    setBusy(true);
    try {
      let saved = 0, blocked = 0;
      for (const p of editedPeriods) {
        if (isClosed(Number(p))) { blocked++; continue; }
        const base = active.metrados?.[String(p)] || {};
        await api.put(`/valorizaciones/projects/${active.projectId}/metrados/${p}`, { metrados: { ...base, ...edits[p] } });
        saved++;
      }
      const { data } = await api.get(`/valorizaciones/projects/${active.projectId}`);
      setActive(data.project); setEdits({}); await recompute(active.projectId, period);
      toast.success(`Metrados guardados en ${saved} periodo(s)${blocked ? ` · ${blocked} cerrado(s) protegido(s)` : ""} y recalculados (motor certificado)`);
    } catch (e) { toast.error("No se pudieron guardar los metrados"); }
    finally { setBusy(false); }
  };

  const allPeriods = useMemo(() => (active ? periodsOf(active) : [1]), [active]);
  const partidas = active?.budget?.filter((b) => b.is_partida !== false) || [];
  const budgetRows = active?.budget || [];
  // §9 · V1 editable solo si aún no hay valorizaciones (metrados) registradas
  const budgetEditable = role !== "AUDITOR" && Object.keys(active?.metrados || {}).length === 0;
  const codeDepth = (c) => (String(c || "").match(/\./g) || []).length;
  const presMap = useMemo(() => { const m = {}; (vlz?.lines || []).forEach((l) => (m[l.line_id] = Number(l.presupuesto || 0))); return m; }, [vlz]);
  const groupSubtotal = (g) => budgetRows.filter((r) => r.is_partida !== false && String(r.code || "").startsWith(g.code + ".")).reduce((s, r) => s + (presMap[r.line_id] || 0), 0);
  // §4/§17 · jerarquía derivada de los códigos (presentación, sin alterar datos): 01=Capítulo, 01.01=Subcapítulo, 01.01.01=Partida
  // §4/§17 · Jerarquía Capítulo→Subcapítulo→Partida.
  // Precedencia: (1) estructura importada (filas agrupadoras is_partida===false) y niveles de fila;
  // (2) el código solo como apoyo/fallback cuando NO hay agrupadores explícitos.
  // Nunca se convierte una fila agrupadora en partida ejecutable.
  const budgetTree = useMemo(() => {
    if (budgetRows.some((b) => b.is_partida === false)) {
      return budgetRows.map((b) => ({
        ...b,
        group: b.is_partida === false,
        level: typeof b.level === "number" ? b.level : (String(b.code || "").match(/\./g) || []).length,
      }));
    }
    const out = []; let cap = null, sub = null;
    for (const b of budgetRows) {
      const parts = String(b.code || "").split(".");
      const capC = parts[0];
      const subC = parts.length >= 3 ? parts.slice(0, 2).join(".") : null;
      if (capC && capC !== cap) { out.push({ group: true, level: 0, code: capC }); cap = capC; sub = null; }
      if (subC && subC !== sub) { out.push({ group: true, level: 1, code: subC }); sub = subC; }
      out.push({ ...b, group: false, level: Math.max(0, parts.length - 1) });
    }
    return out;
  }, [budgetRows]);
  const isHidden = (code) => budgetTree.some((r) => r.group && collapsedGroups[r.code] && code !== r.code && String(code || "").startsWith(r.code + "."));
  const budgetCell = (lid, field, fallback) => (budgetEdits[lid] && budgetEdits[lid][field] !== undefined ? budgetEdits[lid][field] : fallback);
  const setBudgetCell = (lid, field, val) => setBudgetEdits((s) => ({ ...s, [lid]: { ...(s[lid] || {}), [field]: val } }));
  const saveBudget = async () => {
    if (!active || !budgetEditable) return;
    if (Object.keys(budgetEdits).length === 0) { toast.info("Sin cambios en el presupuesto"); return; }
    setBusy(true);
    try {
      const partidasBody = budgetRows.map((b) => ({
        line_id: b.line_id, is_partida: b.is_partida !== false,
        code: budgetCell(b.line_id, "code", b.code), description: budgetCell(b.line_id, "description", b.description),
        unit: budgetCell(b.line_id, "unit", b.unit), m_c: budgetCell(b.line_id, "m_c", b.m_c), pu: budgetCell(b.line_id, "pu", b.pu),
      }));
      await api.post(`/valorizaciones/projects/${active.projectId}/budget`, { partidas: partidasBody, confirmImpact: true });
      const { data } = await api.get(`/valorizaciones/projects/${active.projectId}`);
      setActive(data.project); setBudgetEdits({}); await recompute(active.projectId, period);
      const { data: p } = await api.get(`/valorizaciones/projects/${active.projectId}/presupuesto-pie`); setPie(p);
      toast.success("Presupuesto guardado y recalculado (motor certificado)");
    } catch (e) { toast.error(e?.response?.status === 409 ? "Modificación afecta historia contractual" : "No se pudo guardar el presupuesto"); }
    finally { setBusy(false); }
  };
  const gridRows = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return partidas;
    return partidas.filter((b) => (b.code || "").toLowerCase().includes(q) || (b.description || "").toLowerCase().includes(q));
  }, [partidas, search]);

  /* ── Pantalla de selección de proyectos (clara) ─────────────────────────── */
  if (!active) {
    return (
      <div className="min-h-screen" style={{ background: BG, color: INK }}>
        <div className="mx-auto max-w-6xl px-5 py-6">
          <button onClick={() => navigate("/software")} data-testid="vlz-back-software"
            className="flex items-center gap-1 text-[13px] text-slate-500 hover:text-[#0b3a4d] mb-4">
            <ChevronLeft size={16} /> Volver a ConstruLink Software
          </button>
          <div className="flex items-center gap-3 mb-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: NAVY }}><Calculator size={20} color="#4ade80" /></div>
            <h1 className="text-2xl font-extrabold text-[#0b3a4d]">COREX · Valorizaciones</h1>
          </div>
          <p className="text-[13px] text-slate-500 mb-6">
            Valorizaciones de Obra Val.01 → Val.n · Motor decimal certificado · Fórmula Polinómica · Reajustes.
          </p>
          <div className="flex flex-wrap gap-2 mb-6">
            {role === "AUDITOR" ? (
              <div className={`${CARD} px-3 py-2 text-[12px] text-slate-500`} data-testid="vlz-auditor-readonly">
                🔎 Rol AUDITOR · solo lectura. Puede revisar todos los proyectos, índices PENDIENTES y trazabilidad técnica, pero no crear ni modificar.
              </div>
            ) : (
              <>
                <button onClick={createProject} disabled={busy} data-testid="vlz-create-project"
                  className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-[13px] font-bold text-white hover:bg-emerald-500 disabled:opacity-50">
                  <FolderPlus size={16} /> Nuevo proyecto
                </button>
                <button onClick={seedGolden} disabled={busy} data-testid="vlz-seed-golden"
                  className="flex items-center gap-2 rounded-lg border border-emerald-500/60 px-4 py-2 text-[13px] font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">
                  <Sparkles size={16} /> Cargar expediente Golden (Ayacucho)
                </button>
              </>
            )}
            <button onClick={loadProjects} className={btnGhost}><RefreshCw size={14} /> Actualizar</button>
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Cargando…</div>
          ) : projects.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-slate-500 text-[13px]">
              Aún no tienes proyectos. Crea uno o carga el expediente Golden.
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="vlz-projects-grid">
              {projects.map((p) => (
                <div key={p.projectId} className={`${CARD} p-3 hover:border-emerald-500/60 transition-colors`}>
                  <div className="flex items-start justify-between gap-2">
                    <button onClick={() => openProject(p.projectId)} data-testid={`vlz-open-${p.projectId}`} className="text-left flex-1">
                      <div className="text-[13px] font-bold text-[#17263A] leading-tight">{p.name}</div>
                      <div className="text-[11px] text-slate-500 mt-1">{p.periods} periodo(s) · modo {p.mode}</div>
                    </button>
                    <button onClick={() => deleteProject(p.projectId)} data-testid={`vlz-delete-${p.projectId}`} className="text-slate-400 hover:text-red-500">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ── Workspace ───────────────────────────────────────────────────────── */
  const h = active.header || {};
  const estadoLabel = isClosed(period) ? "Cerrada" : (active.status_periods?.[String(period)] === "CALCULADA" ? "Calculada" : "Pendiente");
  const estadoTone = isClosed(period) ? "bg-slate-200 text-slate-600" : (estadoLabel === "Calculada" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700");
  const conReaj = active.mode !== "directa";
  const activeLabel = labelOf(mod);

  const seg = (on) => `px-3 py-1 text-[12px] font-semibold rounded ${on ? "bg-emerald-600 text-white" : "text-slate-500 hover:text-[#17263A]"}`;

  /* Pantalla 02 contextual */
  const renderAux = () => {
    const e = vlz?.economic || {};
    switch (mod) {
      case "valorizacion": return null;
      case "inicio": return (
        <div className="space-y-1.5" data-testid="vlz-aux-inicio">
          <AuxRow label="Partidas" value={partidas.length} />
          <AuxRow label="Periodos" value={allPeriods.length} />
        </div>
      );
      case "metrados": {
        const totPres = (vlz?.lines || []).reduce((s, r) => s + Number(r.presupuesto || 0), 0);
        const totAcum = (vlz?.lines || []).reduce((s, r) => s + Number(r.acumulado?.valor || 0), 0);
        const totSaldo = (vlz?.lines || []).reduce((s, r) => s + Number(r.saldo?.valor || 0), 0);
        return (<div className="space-y-1.5" data-testid="vlz-aux-metrados">
          <AuxRow label="Contractual (presupuesto)" value={money(totPres)} />
          <AuxRow label="Ejecutado acumulado" value={money(totAcum)} tone="green" />
          <AuxRow label="Saldo por ejecutar" value={money(totSaldo)} strong />
        </div>);
      }
      case "presupuesto": {
        const ad = active.adendas || [];
        const last = ad[ad.length - 1];
        return (<div className="space-y-1.5" data-testid="vlz-aux-presupuesto">
          <AuxRow label="Versión" value={`${active.contract?.version || "V1"} — ${active.contract?.version && active.contract.version !== "V1" ? "Vigente" : "Original"}`} strong />
          <AuxRow label="Estado" value="Vigente" tone="green" />
          <AuxRow label="Adenda vigente" value={last ? `N.º ${last.numero || ad.length}` : "Ninguna"} />
          <AuxRow label="Vigente desde" value={last?.from_period ? `Val.${String(last.from_period).padStart(2, "0")}` : "Inicio contractual"} />
          <AuxRow label="Partidas" value={partidas.length} />
        </div>);
      }
      case "fp": return (<div className="space-y-1.5" data-testid="vlz-aux-fp">
        <AuxRow label="Factor K (periodo)" value={proc?.fp?.k_almacenado || vlz?.k || "—"} strong tone="green" />
        <AuxRow label="Mes IU" value={proc?.fp?.iu_month || "—"} />
        <AuxRow label="Mes base" value={proc?.fp?.base_month || "—"} />
        <AuxRow label="Conciliación" value={proc?.fp ? "K COREX = K Excel" : "—"} />
      </div>);
      case "reajuste": return (<div className="space-y-1.5" data-testid="vlz-aux-reajuste">
        <AuxRow label="V (costo obra)" value={money(proc?.reajuste?.V)} />
        <AuxRow label="K" value={proc?.reajuste?.K || "—"} />
        <AuxRow label="Reajuste = V×(K−1)" value={money(proc?.reajuste?.resultado)} strong tone="green" />
      </div>);
      case "reintegro": return (<div className="space-y-1.5" data-testid="vlz-aux-reintegro">
        <AuxRow label="Origen → Reconocido" value={proc?.reintegro ? `Val.${proc.reintegro.origen_periodo} → ${proc.reintegro.reconocido_en}` : "—"} />
        <AuxRow label="K orig → K act" value={proc?.reintegro ? `${proc.reintegro.K_original} → ${proc.reintegro.K_actualizado}` : "—"} />
        <AuxRow label="Diferencia" value={money(proc?.reintegro?.resultado)} strong tone="green" />
      </div>);
      case "amortizacion": {
        const last = proc?.amortizacion?.rows?.[proc.amortizacion.rows.length - 1];
        return (<div className="space-y-1.5" data-testid="vlz-aux-amortizacion">
          <AuxRow label="Adelanto Directo (AD)" value={money(proc?.amortizacion?.adelanto_directo)} />
          <AuxRow label="Amortizado acum." value={money(last?.acumulada)} tone="green" />
          <AuxRow label="Saldo por amortizar" value={money(last?.saldo_por_amortizar)} strong />
        </div>);
      }
      case "deduccion": return (<div className="space-y-1.5" data-testid="vlz-aux-deduccion">
        <AuxRow label="Origen" value={proc?.deduccion ? `Val.${proc.deduccion.origen_periodo}` : "—"} />
        <AuxRow label="Amort. afectada" value={money(proc?.deduccion?.amortizacion_afectada)} />
        <AuxRow label="Deducción" value={money(proc?.deduccion?.resultado)} strong tone="red" />
      </div>);
      case "control": case "control-financiero": {
        const rows = controlData?.rows || [];
        const pa = rows.reduce((s, r) => s + Number(r.programado || 0), 0);
        const ea = rows.reduce((s, r) => s + Number(r.ejecutado || 0), 0);
        return (<div className="space-y-1.5" data-testid="vlz-aux-control">
          <AuxRow label="Programado acum." value={money(pa)} />
          <AuxRow label="Ejecutado acum." value={money(ea)} tone="green" />
          <AuxRow label="Desviación" value={money(ea - pa)} strong tone={ea - pa < 0 ? "red" : undefined} />
        </div>);
      }
      case "adelantos": return (<div className="space-y-1.5" data-testid="vlz-aux-adelantos">
        <AuxRow label="Adelanto Directo" value={money(proc?.amortizacion?.adelanto_directo)} strong />
        <AuxRow label="Tasa A.D." value={proc?.amortizacion?.tasa_ad || "—"} />
        <AuxRow label="Contrato (sin IGV)" value={money(proc?.amortizacion?.contrato_sin_igv)} />
      </div>);
      case "indices": return (<div className="space-y-1.5" data-testid="vlz-aux-indices">
        <AuxRow label="Régimen" value={iu.regime === "1992" ? "1992 (6 áreas)" : "2025 (13 áreas)"} />
        <AuxRow label="Base" value={areasInfo?.base || "—"} />
        <AuxRow label="Área" value={iu.area} />
        <AuxRow label="Índices cargados" value={iu.items.length} />
      </div>);
      case "adendas": return (<div className="space-y-1.5" data-testid="vlz-aux-adendas">
        <AuxRow label="Versión contractual" value={active.contract?.version || "V1 (base)"} strong />
        <AuxRow label="Adendas registradas" value={(active.adendas || []).length} />
      </div>);
      case "auditoria": return (<div className="space-y-1.5" data-testid="vlz-aux-auditoria">
        <AuxRow label="Eventos registrados" value={audit?.length ?? "—"} strong />
        <AuxRow label="Último evento" value={audit?.[0]?.action || "—"} />
      </div>);
      case "configuracion": return (<div className="space-y-1.5" data-testid="vlz-aux-config">
        <AuxRow label="Modo de valorización" value={conReaj ? "Con reajustes" : "Directa"} strong />
        <AuxRow label="Modo Técnico" value={modoTecnico ? "ON" : "OFF"} tone={modoTecnico ? "green" : undefined} />
        <AuxRow label="Distribución" value="75 / 25 (fija)" />
      </div>);
      case "usuarios": return (<div className="space-y-1.5" data-testid="vlz-aux-usuarios">
        <AuxRow label="Rol activo" value={role} strong />
        <AuxRow label="Permisos" value={role === "AUDITOR" ? "Solo lectura" : role === "ADMINISTRADOR" ? "Completo" : "Edición"} />
      </div>);
      default: return null;
    }
  };

  /* Toolbar contextual (§12) */
  const toolbarActions = () => {
    const items = [];
    if (mod === "presupuesto") {
      items.push(<button key="imp" onClick={openFileImport} data-testid="vlz-import-presupuesto" className={btnGhost}><Download size={13} /> Importar Presupuesto</button>);
      items.push(<button key="pega" onClick={importPaste} data-testid="vlz-presu-pegar" className={btnGhost}><ClipboardPaste size={13} /> Pegar desde Excel</button>);
      if (budgetEditable) items.push(<button key="sav" onClick={saveBudget} disabled={busy} data-testid="vlz-presu-guardar" className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-[12px] font-bold text-white hover:bg-emerald-500 disabled:opacity-50"><Save size={13} /> Guardar presupuesto</button>);
    }
    if (mod === "metrados") {
      items.push(<button key="pega" onClick={importPaste} data-testid="vlz-import-metrados" className={btnGhost}><ClipboardPaste size={13} /> Pegar desde Excel</button>);
      items.push(<button key="und" onClick={undo} data-testid="vlz-undo" title="Deshacer (Ctrl+Z)" className={btnGhost}><Undo2 size={13} /> Deshacer</button>);
      items.push(<button key="red" onClick={redo} data-testid="vlz-redo" title="Rehacer (Ctrl+Y)" className={btnGhost}><Redo2 size={13} /> Rehacer</button>);
      items.push(<button key="cop" onClick={copySelection} data-testid="vlz-copy" className={btnGhost}><Copy size={13} /> Copiar</button>);
      items.push(<button key="cut" onClick={cutSelection} disabled={role === "AUDITOR"} data-testid="vlz-cut" className={btnGhost}><Scissors size={13} /> Cortar</button>);
      items.push(<button key="sav" onClick={saveMetrados} disabled={busy || role === "AUDITOR"} data-testid="vlz-save-metrados" className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-[12px] font-bold text-white hover:bg-emerald-500 disabled:opacity-50"><Save size={13} /> Guardar</button>);
    }
    if (mod === "valorizacion") items.push(<button key="vp" onClick={() => exportFile("pdf")} data-testid="vlz-vista-previa" className={btnGhost}><Printer size={13} /> Vista Previa</button>);
    if (mod === "indices" && role === "ADMINISTRADOR") items.push(<button key="upd" onClick={updateIU} data-testid="vlz-actualizar-iu" className={btnGhost}><RefreshCw size={13} /> Actualizar Índices Unificados</button>);
    if (mod === "adendas") items.push(<button key="ad" onClick={() => { }} data-testid="vlz-registrar-adenda" className={btnGhost}><FileStack size={13} /> Registrar Adenda</button>);
    if (modoTecnicoCtl.puede_ver)
      items.push(<button key="mt" onClick={toggleModoTecnico} disabled={!modoTecnicoCtl.puede_alternar} data-testid="vlz-modo-tecnico-toggle"
        className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12px] font-semibold ${modoTecnico ? "bg-emerald-600 text-white" : "border border-slate-200 bg-white text-slate-600"} ${modoTecnicoCtl.puede_alternar ? "hover:brightness-105" : "opacity-70 cursor-default"}`}>
        Modo Técnico: {modoTecnico ? "ON" : "OFF"}</button>);
    return items;
  };

  const H = "px-2 py-1 text-right tabular-nums min-w-[80px]";
  const pc = (x) => `${(Number(x) * 100).toFixed(2)}%`;

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: BG, color: INK }}>
      {/* ── SIDEBAR (§9) ───────────────────────────────────────────── */}
      <aside className={`flex flex-col ${collapsed ? "w-[60px]" : "w-[228px]"} shrink-0 transition-[width] duration-200`} style={{ background: NAVY }} data-testid="vlz-sidebar">
        <div className="flex items-center gap-2 px-3 h-14 border-b border-white/10">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-emerald-500/90 shrink-0"><Calculator size={17} color="#052e21" /></div>
          {!collapsed && <div className="leading-tight"><div className="text-[15px] font-extrabold text-white tracking-wide">COREX</div><div className="text-[9px] text-emerald-300 uppercase tracking-wider">Valorizaciones</div></div>}
        </div>
        <nav className="flex-1 overflow-y-auto py-2" data-testid="vlz-sidebar-nav">
          {NAV_TREE.map((n) => {
            const Icon = n.icon;
            if (!n.children) {
              const on = mod === n.key;
              return (
                <button key={n.key} onClick={() => setMod(n.key)} data-testid={`vlz-nav-${n.key}`} title={n.label}
                  className={`group relative flex w-full items-center gap-3 px-3 py-2 text-[12.5px] font-semibold transition-colors ${on ? "bg-emerald-500/15 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
                  {on && <span className="absolute left-0 top-0 h-full w-[3px] bg-emerald-400" />}
                  <Icon size={17} className={on ? "text-emerald-300" : "text-slate-400 group-hover:text-slate-200"} />
                  {!collapsed && <span className="truncate">{n.label}</span>}
                </button>
              );
            }
            const open = !!openGroups[n.key];
            const activeInGroup = n.children.some((c) => c.key === mod);
            return (
              <div key={n.key}>
                <button data-testid={`vlz-group-${n.key}`} title={n.label}
                  onClick={() => { if (collapsed) { setCollapsed(false); setOpenGroups((s) => ({ ...s, [n.key]: true })); } else setOpenGroups((s) => ({ ...s, [n.key]: !s[n.key] })); }}
                  className={`group relative flex w-full items-center gap-3 px-3 py-2 text-[11px] font-bold uppercase tracking-wide transition-colors ${activeInGroup ? "text-white" : "text-slate-300 hover:text-white"}`}>
                  {activeInGroup && <span className="absolute left-0 top-0 h-full w-[3px] bg-emerald-400" />}
                  <Icon size={17} className={activeInGroup ? "text-emerald-300" : "text-slate-400 group-hover:text-slate-200"} />
                  {!collapsed && <><span className="flex-1 truncate text-left">{n.label}</span>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</>}
                </button>
                {!collapsed && open && n.children.map((c) => {
                  const on = mod === c.key;
                  return (
                    <button key={c.key} onClick={() => setMod(c.key)} data-testid={`vlz-nav-${c.key}`} title={c.label}
                      className={`flex w-full items-center gap-2 py-1.5 pl-10 pr-3 text-[12px] transition-colors ${on ? "bg-emerald-500/15 font-semibold text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${on ? "bg-emerald-400" : "border border-slate-500"}`} />
                      <span className="truncate">{c.label}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <button onClick={() => setCollapsed((c) => !c)} data-testid="vlz-sidebar-collapse"
          className="flex items-center gap-2 px-3 h-11 border-t border-white/10 text-[11px] text-slate-400 hover:text-white">
          {collapsed ? <ChevronRight size={16} /> : <><PanelLeft size={15} /> Colapsar</>}
        </button>
      </aside>

      {/* ── COLUMNA PRINCIPAL ──────────────────────────────────────── */}
      <div className="flex flex-1 flex-col min-w-0">
        {/* HEADER (§11) */}
        <header className="flex items-center gap-x-3 gap-y-1 flex-wrap px-4 h-14 shrink-0 text-white" style={{ background: NAVY }} data-testid="vlz-header">
          <button onClick={() => { setActive(null); setVlz(null); }} data-testid="vlz-back-projects" className="flex items-center gap-1 text-[12px] text-slate-300 hover:text-white">
            <ChevronLeft size={15} /> Proyectos
          </button>
          <div className="h-6 w-px bg-white/15" />
          <div className="min-w-0">
            <div className="text-[12.5px] font-bold truncate max-w-[280px]" title={h.obra || active.name}>{h.obra || active.name}</div>
            {h.entidad && <div className="text-[10px] text-slate-400 truncate max-w-[280px]">{h.entidad}</div>}
          </div>
          <select value={period} onChange={(e) => changePeriod(Number(e.target.value))} data-testid="vlz-period-select"
            className="rounded-md border border-white/20 bg-white/10 px-2 py-1 text-[12px] font-semibold text-white focus:outline-none">
            {allPeriods.map((p) => <option key={p} value={p} className="text-black">Valorización N.º {String(p).padStart(2, "0")}</option>)}
          </select>
          <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${estadoTone}`} data-testid="vlz-estado-badge">{estadoLabel}</span>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={saveMetrados} disabled={busy || role === "AUDITOR"} data-testid="vlz-header-guardar"
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-[12px] font-bold text-white hover:bg-blue-500 disabled:opacity-50"><Save size={14} /> Guardar</button>
            <button onClick={calcular} disabled={busy} data-testid="vlz-header-calcular"
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-1.5 text-[12px] font-bold text-[#062330] hover:bg-emerald-400 disabled:opacity-50"><Calculator size={14} /> Calcular</button>
            <button onClick={() => exportFile("pdf")} data-testid="vlz-export-pdf" className="inline-flex items-center gap-1.5 rounded-md border border-white/20 bg-white/10 px-2.5 py-1.5 text-[12px] font-medium text-white hover:bg-white/20"><Printer size={13} /> PDF</button>
            <button onClick={() => exportFile("auditable")} data-testid="vlz-export-excel" className="inline-flex items-center gap-1.5 rounded-md border border-white/20 bg-white/10 px-2.5 py-1.5 text-[12px] font-medium text-white hover:bg-white/20"><FileSpreadsheet size={13} /> Excel</button>
            <div className="relative">
              <button onClick={() => setMenuOpen((o) => !o)} data-testid="vlz-header-menu" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/20 bg-white/10 text-white hover:bg-white/20"><Menu size={16} /></button>
              {menuOpen && (
                <div className="absolute right-0 mt-1 w-52 rounded-md border border-slate-200 bg-white py-1 text-[12px] text-slate-700 shadow-lg z-30" data-testid="vlz-header-menu-panel" onMouseLeave={() => setMenuOpen(false)}>
                  <button onClick={() => { importPaste(); setMenuOpen(false); }} className="flex w-full items-center gap-2 px-3 py-1.5 hover:bg-slate-50"><ClipboardPaste size={13} /> Pegar desde Excel</button>
                  <button onClick={() => { exportFile("auditable"); setMenuOpen(false); }} className="flex w-full items-center gap-2 px-3 py-1.5 hover:bg-slate-50"><Download size={13} /> Exportar auditable</button>
                  <button onClick={() => { setMod("reportes"); setMenuOpen(false); }} className="flex w-full items-center gap-2 px-3 py-1.5 hover:bg-slate-50"><FileBarChart size={13} /> Centro de reportes</button>
                  <div className="my-1 border-t border-slate-100" />
                  <span className="block px-3 py-1 text-[10px] uppercase text-slate-400">Rol: {role}</span>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* TOOLBAR CONTEXTUAL (§12) */}
        <div className="flex items-center gap-2 flex-wrap px-4 py-2 border-b border-slate-200 bg-[#eef2f7]" data-testid="vlz-toolbar">
          {["presupuesto", "adendas"].includes(mod) ? (
            <span className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-[#0b3a4d]" data-testid="vlz-version-chip">
              <FileStack size={13} /> Versión contractual: {active.contract?.version || "V1"} — {active.contract?.version && active.contract.version !== "V1" ? "Vigente" : "Original"}
            </span>
          ) : (
            <>
              <span className="text-[11px] text-slate-500 font-medium">Modo de valorización</span>
              <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="vlz-modo-valorizacion">
                <button onClick={() => conReaj && toggleMode()} data-testid="vlz-modo-directa" className={seg(!conReaj)}>Directa</button>
                <button onClick={() => !conReaj && toggleMode()} data-testid="vlz-modo-reajustes" className={seg(conReaj)}>Con reajustes</button>
              </div>
              {conReaj && (
                <div className="hidden lg:flex items-center gap-1.5 pl-2" data-testid="vlz-modo-chips">
                  {["Fórmula polinómica", "Reajuste", "Reintegro", "Amortización", "Deducción"].map((c) => (
                    <span key={c} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 border border-emerald-200"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{c}</span>
                  ))}
                </div>
              )}
            </>
          )}
          <div className="ml-auto flex items-center gap-1.5 flex-wrap">
            {toolbarActions()}
          </div>
        </div>

        {/* BODY: PANTALLA 01 (75%) + PANTALLA 02 (25%) */}
        <div className="flex flex-1 gap-3 p-3 min-h-0" data-testid="vlz-layout-root" data-layout="75/25">
          {/* PANTALLA 01 */}
          <section className="flex flex-col min-w-0 overflow-y-auto overflow-x-hidden" style={{ flex: "3 1 0%" }} data-testid="vlz-pantalla-01">
            {busy && <div className="mb-2 flex items-center gap-2 text-[12px] text-slate-500"><Loader2 className="animate-spin" size={14} /> Procesando…</div>}
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-[13px] font-bold text-[#0b3a4d] uppercase tracking-wide">{activeLabel}</h2>
              {(mod === "valorizacion" || mod === "metrados" || PROC_MODS.includes(mod)) &&
                <span className="text-[11px] text-slate-400">· Valorización N.º {String(period).padStart(2, "0")}</span>}
            </div>

            {/* INICIO */}
            {mod === "inicio" && (
              <div className="space-y-3" data-testid="vlz-inicio">
                <div className={`${CARD} p-3 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-[10px]`}>
                  {[["OBRA", h.obra], ["ENTIDAD", h.entidad], ["CONTRATISTA", h.contratista], ["UBICACIÓN", h.ubicacion], ["PERIODO", `VLRZ N.º ${String(period).padStart(2, "0")}`], ["MODALIDAD", h.modalidad || active.mode], ["MONTO CONTRATADO", h.monto_contratado], ["RESIDENTE", h.residente]].filter(([, v]) => v).map(([l, v]) => (
                    <div key={l} className="flex flex-col"><span className="text-slate-400 uppercase">{l}</span><span className="text-[#17263A] font-medium truncate" title={String(v)}>{typeof v === "string" && /^\d/.test(v) && l.includes("MONTO") ? `S/ ${n2(v)}` : v}</span></div>
                  ))}
                </div>
                {proc?.amortizacion && (
                  <div className={`${CARD} p-3`} data-testid="vlz-adelantos">
                    <div className={`${secTitle} mb-2`}>Adelantos</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {[["Adelanto Directo (AD)", money(proc.amortizacion.adelanto_directo)], ["Tasa A.D.", proc.amortizacion.tasa_ad], ["Contrato (sin IGV)", money(proc.amortizacion.contrato_sin_igv)]].map(([l, v]) => (
                        <div key={l} className="rounded border border-slate-200 bg-slate-50 p-2"><div className="text-[10px] uppercase text-slate-400">{l}</div><div className="text-[14px] font-extrabold text-[#17263A] tabular-nums">{v}</div></div>
                      ))}
                    </div>
                    <div className="mt-1 text-[10px] text-slate-400">El Adelanto Directo se amortiza en cada valorización (A = AD × V / C). Detalle en Cálculos de Reajuste › Amortización A.D.</div>
                  </div>
                )}
              </div>
            )}

            {/* VALORIZACIÓN */}
            {mod === "valorizacion" && (
              <div className="space-y-3">
                <div className={`${CARD} p-2 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-0.5 text-[10px]`} data-testid="vlz-encabezado">
                  {[["OBRA", h.obra], ["ENTIDAD", h.entidad], ["CONTRATISTA", h.contratista], ["UBICACIÓN", h.ubicacion], ["PERIODO", `VLRZ N.º ${String(period).padStart(2, "0")}`], ["MODALIDAD", h.modalidad || active.mode], ["MONTO CONTRATADO (c/IGV)", h.monto_contratado], ["RESIDENTE", h.residente]].filter(([, v]) => v).map(([l, v]) => (
                    <div key={l} className="flex flex-col"><span className="text-slate-400 uppercase">{l}</span><span className="text-[#17263A] truncate" title={String(v)}>{typeof v === "string" && /^\d/.test(v) && l.includes("MONTO") ? `S/ ${n2(v)}` : v}</span></div>
                  ))}
                </div>
                <div className={CARD} data-testid="vlz-matrix">
                  {(() => {
                    const ROW_H = 30, VIEW = 560;
                    const lines = vlz?.lines || [];
                    const total = lines.length;
                    const virt = total > 120;
                    const start = virt ? Math.max(0, Math.floor(vScrollV / ROW_H) - 8) : 0;
                    const end = virt ? Math.min(total, start + Math.ceil(VIEW / ROW_H) + 16) : total;
                    const topPad = start * ROW_H, botPad = (total - end) * ROW_H;
                    return (
                      <div className="overflow-auto rounded-lg" style={{ maxHeight: VIEW }}
                        onScroll={(e) => virt && setVScrollV(e.currentTarget.scrollTop)} data-testid="vlz-matrix-scroll">
                        <div className="px-2 py-1 text-[10px] text-slate-500 bg-slate-50 border-b border-slate-200">
                          {total} partidas{virt ? " · virtualización activa" : ""} · estructura completa (Presupuesto · Anterior · Actual · Acumulado · Saldo) · scroll H/V interno
                        </div>
                        <table className="border-collapse text-[10px] min-w-max">
                          <thead className={`sticky top-0 ${THEAD} z-10`}>
                            <tr className="text-[9px] uppercase">
                              <th className={`px-2 py-1 text-left sticky left-0 ${THEAD} z-20 min-w-[56px]`} rowSpan={3}>Item</th>
                              <th className="px-2 py-1 text-left min-w-[220px]" rowSpan={3}>Partida</th>
                              <th className="px-2 py-1 text-center border-l border-white/20 bg-white/10" colSpan={4} rowSpan={2}>Presupuesto</th>
                              <th className="px-2 py-1 text-center border-l border-white/20 bg-white/5" colSpan={9}>Avances</th>
                              <th className="px-2 py-1 text-center border-l border-white/20 bg-white/10" colSpan={3} rowSpan={2}>Saldo</th>
                            </tr>
                            <tr className="text-[9px] uppercase">
                              <th className="px-2 py-1 text-center border-l border-white/20" colSpan={3}>Anterior</th>
                              <th className="px-2 py-1 text-center border-l border-white/20 bg-emerald-500/25 text-emerald-200" colSpan={3}>Actual</th>
                              <th className="px-2 py-1 text-center border-l border-white/20" colSpan={3}>Acumulado</th>
                            </tr>
                            <tr className="text-[9px]">
                              {["Und.", "Metrado", "P.Unit.", "Presup."].map((hd, i) => <th key={hd} className={`px-2 py-1 text-right min-w-[64px] ${i === 0 ? "border-l border-white/20" : ""}`}>{hd}</th>)}
                              {["Metrado", "Valor S/", "%"].map((hd, i) => <th key={"a" + hd} className={`px-2 py-1 text-right min-w-[64px] ${i === 0 ? "border-l border-white/20" : ""}`}>{hd}</th>)}
                              {["Metrado", "Valor S/", "%"].map((hd, i) => <th key={"c" + hd} className={`px-2 py-1 text-right min-w-[64px] bg-emerald-500/20 ${i === 0 ? "border-l border-white/20" : ""}`}>{hd}</th>)}
                              {["Metrado", "Valor S/", "%"].map((hd, i) => <th key={"m" + hd} className={`px-2 py-1 text-right min-w-[64px] ${i === 0 ? "border-l border-white/20" : ""}`}>{hd}</th>)}
                              {["Metrado", "Valor S/", "%"].map((hd, i) => <th key={"s" + hd} className={`px-2 py-1 text-right min-w-[64px] ${i === 0 ? "border-l border-white/20" : ""}`}>{hd}</th>)}
                            </tr>
                          </thead>
                          <tbody className="text-[#17263A]">
                            {topPad > 0 && <tr style={{ height: topPad }} aria-hidden><td colSpan={18} /></tr>}
                            {lines.slice(start, end).map((r) => (
                              <tr key={r.line_id} style={{ height: ROW_H }} className={`border-t border-slate-100 ${r.sobrevalorizacion ? "bg-red-50" : "hover:bg-slate-50"}`} data-testid={`vlz-line-${r.line_id}`}>
                                <td className="px-2 py-1 font-mono text-slate-500 sticky left-0 bg-white">{r.code}</td>
                                <td className="px-2 py-1 text-[#17263A] max-w-[240px] min-w-[220px] truncate" title={r.description}>{r.description}</td>
                                <td className="px-2 py-1 text-slate-500 border-l border-slate-100">{r.unit}</td>
                                <td className={H}>{n2(r.m_c)}</td>
                                <td className={H + " text-slate-500"}>{n2(r.pu)}</td>
                                <td className={H}>{n2(r.presupuesto)}</td>
                                <td className={H + " border-l border-slate-100"}>{n2(r.anterior.metrado)}</td>
                                <td className={H}>{n2(r.anterior.valor)}</td>
                                <td className={H + " text-slate-500"}>{pc(r.anterior.pct)}</td>
                                <td className={H + " border-l border-slate-100 bg-emerald-50"}>{n2(r.actual.metrado)}</td>
                                <td className={H + " font-semibold text-emerald-700 bg-emerald-50"}>{n2(r.actual.valor)}</td>
                                <td className={H + " text-emerald-600 bg-emerald-50"}>{pc(r.actual.pct)}</td>
                                <td className={H + " border-l border-slate-100"}>{n2(r.acumulado.metrado)}</td>
                                <td className={H}>{n2(r.acumulado.valor)}</td>
                                <td className={H + " text-slate-500"}>{pc(r.acumulado.pct)}</td>
                                <td className={H + " border-l border-slate-100"}>{n2(r.saldo.metrado)}</td>
                                <td className={H + " text-slate-500"}>{n2(r.saldo.valor)}</td>
                                <td className={H + " text-slate-400"}>{pc(r.saldo.pct)}</td>
                              </tr>
                            ))}
                            {botPad > 0 && <tr style={{ height: botPad }} aria-hidden><td colSpan={18} /></tr>}
                          </tbody>
                          <tfoot className="sticky bottom-0 bg-slate-100 font-bold text-[#17263A]">
                            <tr>
                              <td className="px-2 py-1" colSpan={5}>COSTO DIRECTO</td>
                              <td className={H} title="Presupuesto (CD contractual)">{vlz && n2(active.contract?.cd_contractual)}</td>
                              <td className="px-2 py-1 border-l border-slate-200" colSpan={3}></td>
                              <td className={H + " border-l border-slate-200 text-emerald-700"} colSpan={3} title="CD Actual valorizado">{vlz && n2(vlz.cd)}</td>
                              <td className="px-2 py-1 border-l border-slate-200" colSpan={3}></td>
                              <td className="px-2 py-1 border-l border-slate-200" colSpan={3}></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    );
                  })()}
                </div>
                {/* Detalle de partida (§20 · panel desplegable, no otra valorización) */}
                <div className={CARD} data-testid="vlz-detalle-partida">
                  <button onClick={() => setShowDetalle((s) => !s)} data-testid="vlz-detalle-toggle" className="flex w-full items-center justify-between px-3 py-2 text-[12px] font-semibold text-[#0b3a4d]">
                    <span>Detalle de cálculo por partida</span>
                    <ChevronRight size={15} className={`transition-transform ${showDetalle ? "rotate-90" : ""}`} />
                  </button>
                  {showDetalle && (
                    <div className="border-t border-slate-200 p-3" data-testid="vlz-proc-partida">
                      <select data-testid="vlz-proc-partida-select" value={procRow || ""} onChange={(e) => setProcRow(e.target.value)}
                        className="mb-2 rounded border border-slate-300 bg-white px-2 py-1 text-[11px] text-[#17263A] max-w-[360px]">
                        <option value="">— Selecciona una partida —</option>
                        {(vlz?.lines || []).map((r) => <option key={r.line_id} value={r.line_id}>{r.code} · {r.description.slice(0, 48)}</option>)}
                      </select>
                      {(() => {
                        const r = (vlz?.lines || []).find((x) => x.line_id === procRow);
                        if (!r) return <div className="text-[11px] text-slate-400">Elige una partida para ver su desglose.</div>;
                        const F = ({ label, op, res }) => (
                          <div className="flex flex-col rounded border border-slate-200 bg-slate-50 px-2 py-1">
                            <span className="text-[10px] uppercase text-slate-400">{label}</span>
                            <span className="text-[11px] text-slate-600">{op}</span>
                            <span className="text-[12px] font-bold text-emerald-700 tabular-nums">{res}</span>
                          </div>
                        );
                        return (
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="vlz-proc-partida-detail">
                            <F label="Presupuesto (Metrado × P.U.)" op={`${n2(r.m_c)} × ${n2(r.pu)}`} res={`S/ ${n2(r.presupuesto)}`} />
                            <F label="Valor Anterior" op={`${n2(r.anterior.metrado)} × ${n2(r.pu)}`} res={`S/ ${n2(r.anterior.valor)} · ${(r.anterior.pct * 100).toFixed(2)}%`} />
                            <F label="Valor Actual" op={`${n2(r.actual.metrado)} × ${n2(r.pu)}`} res={`S/ ${n2(r.actual.valor)} · ${(r.actual.pct * 100).toFixed(2)}%`} />
                            <F label="Metrado Acumulado" op={`${n2(r.anterior.metrado)} + ${n2(r.actual.metrado)}`} res={`${n2(r.acumulado.metrado)}`} />
                            <F label="Valor Acumulado" op={`${n2(r.anterior.valor)} + ${n2(r.actual.valor)}`} res={`S/ ${n2(r.acumulado.valor)} · ${(r.acumulado.pct * 100).toFixed(2)}%`} />
                            <F label="Saldo" op={`${n2(r.m_c)} − ${n2(r.acumulado.metrado)}`} res={`S/ ${n2(r.saldo.valor)} · ${(r.saldo.pct * 100).toFixed(2)}%`} />
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* METRADOS */}
            {mod === "metrados" && (
              <div className={CARD} data-testid="vlz-metrados-matrix"
                tabIndex={0}
                onKeyDown={(e) => {
                  const k = e.key.toLowerCase();
                  if ((e.ctrlKey || e.metaKey) && k === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
                  else if ((e.ctrlKey || e.metaKey) && (k === "y" || (e.shiftKey && k === "z"))) { e.preventDefault(); redo(); }
                  else if ((e.ctrlKey || e.metaKey) && k === "c") { copySelection(); }
                  else if ((e.ctrlKey || e.metaKey) && k === "x") { e.preventDefault(); cutSelection(); }
                }}
                onPaste={(e) => { const txt = e.clipboardData?.getData("text"); if (txt && (txt.includes("\n") || txt.includes("\t"))) { e.preventDefault(); pasteBlock(txt); } }}>
                <div className="bg-slate-50 border-b border-slate-200 px-2 py-1.5 flex items-center gap-2 flex-wrap" data-testid="vlz-metrados-help">
                  <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar partida (código o descripción)…" data-testid="vlz-metrados-search"
                    className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] text-[#17263A] w-64 focus:border-emerald-500 focus:outline-none" />
                  <span className="text-[10px] text-slate-400">
                    Selección: clic + arrastre o Shift+clic. Ctrl+C · Ctrl+X · Ctrl+V (bloque M×N) · Ctrl+Z · Ctrl+Y. Enter/Shift+Enter, Ctrl+↑/↓. Períodos CERRADOS protegidos (🔒). {role === "AUDITOR" && "· AUDITOR: solo lectura."}
                  </span>
                  <span className="ml-auto flex items-center gap-2 text-[9px]" data-testid="vlz-editabilidad-legend">
                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-50 border border-emerald-400"></span>Editable</span>
                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-slate-100 border border-slate-300"></span>Cerrado/Solo lectura</span>
                  </span>
                </div>
                {(() => {
                  const ROW_H = 30, VIEW = 540;
                  const total = gridRows.length;
                  const virt = total > 120;
                  const start = virt ? Math.max(0, Math.floor(vScrollM / ROW_H) - 8) : 0;
                  const end = virt ? Math.min(total, start + Math.ceil(VIEW / ROW_H) + 16) : total;
                  const topPad = start * ROW_H, botPad = (total - end) * ROW_H;
                  const fixedCols = 4;
                  return (
                    <div className="overflow-auto" style={{ maxHeight: VIEW }}
                      onScroll={(e) => virt && setVScrollM(e.currentTarget.scrollTop)} data-testid="vlz-metrados-scroll">
                      <div className="px-2 py-0.5 text-[10px] text-slate-400">{total} partidas{search ? " (filtradas)" : ""}{virt ? " · virtualización activa" : ""} · scroll H/V interno</div>
                      <table className="min-w-max border-collapse text-[11px]">
                        <thead className={`sticky top-0 ${THEAD} z-10`}>
                          <tr>
                            <th className={`px-2 py-1 text-left sticky left-0 ${THEAD} z-20 min-w-[56px]`}>Item</th>
                            <th className="px-2 py-1 text-left min-w-[200px]">Partida</th>
                            <th className="px-2 py-1 text-left">Und.</th>
                            <th className="px-2 py-1 text-right min-w-[92px]">Metr. contract.</th>
                            {allPeriods.map((p) => (
                              <th key={p} className={`px-2 py-1 text-right min-w-[92px] ${p === period ? "bg-emerald-500/30 text-emerald-100" : ""}`} data-testid={`vlz-col-${p}`}>
                                VLRZ {String(p).padStart(2, "0")} {isClosed(p) ? "🔒" : ""}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="text-[#17263A]">
                          {topPad > 0 && <tr style={{ height: topPad }} aria-hidden><td colSpan={fixedCols + allPeriods.length} /></tr>}
                          {gridRows.slice(start, end).map((b, li) => {
                            const ri = start + li;
                            return (
                              <tr key={b.line_id} style={{ height: ROW_H }} className="border-t border-slate-100 hover:bg-slate-50">
                                <td className="px-2 py-1 font-mono text-slate-500 sticky left-0 bg-white">{b.code}</td>
                                <td className="px-2 py-1 text-[#17263A] max-w-[220px] min-w-[200px] truncate" title={b.description}>{b.description}</td>
                                <td className="px-2 py-1 text-slate-500">{b.unit}</td>
                                <td className="px-2 py-1 text-right tabular-nums text-slate-600">{n2(b.m_c)}</td>
                                {allPeriods.map((p, ci) => {
                                  const closed = isClosed(p);
                                  const selected = inSelRect(ri, ci);
                                  if (closed) {
                                    const v = (active.metrados?.[String(p)] || {})[b.line_id] || "";
                                    return <td key={p} className="px-2 py-1 text-right tabular-nums text-slate-400 bg-slate-100" title="Periodo cerrado (protegido)">{v ? n2(v) : "—"}</td>;
                                  }
                                  return (
                                    <td key={p} className={`px-1 py-0.5 ${selected ? "bg-emerald-100" : "bg-emerald-50/60"}`}
                                      onMouseDown={(e) => { if (e.shiftKey) setSel((s) => ({ ...s, focus: { r: ri, c: ci } })); else setSel({ anchor: { r: ri, c: ci }, focus: { r: ri, c: ci } }); }}
                                      onMouseEnter={(e) => { if (e.buttons === 1) setSel((s) => (s.anchor ? { ...s, focus: { r: ri, c: ci } } : s)); }}>
                                      <input type="number" step="any"
                                        value={cellVal(p, b.line_id)}
                                        data-testid={`vlz-metrado-${p}-${b.line_id}`}
                                        disabled={role === "AUDITOR"}
                                        title={role === "AUDITOR" ? "AUDITOR: solo lectura" : "Editable (periodo abierto)"}
                                        onFocus={() => setSel((s) => (s.anchor ? s : { anchor: { r: ri, c: ci }, focus: { r: ri, c: ci } }))}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") { e.preventDefault(); focusCell(e.shiftKey ? ri - 1 : ri + 1, ci); }
                                          else if (e.key === "ArrowDown" && e.ctrlKey) { e.preventDefault(); focusCell(ri + 1, ci); }
                                          else if (e.key === "ArrowUp" && e.ctrlKey) { e.preventDefault(); focusCell(ri - 1, ci); }
                                        }}
                                        onChange={(e) => setCell(p, b.line_id, e.target.value)}
                                        className="w-20 rounded border border-slate-300 bg-white px-1 py-0.5 text-right text-[11px] text-[#17263A] focus:border-emerald-500 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100" />
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                          {botPad > 0 && <tr style={{ height: botPad }} aria-hidden><td colSpan={fixedCols + allPeriods.length} /></tr>}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* PRESUPUESTO CONTRACTUAL — jerarquía + Pie (§3–§10, §23–§25) */}
            {mod === "presupuesto" && (
              <div className="space-y-3">
                {/* §10 Identificación de versión contractual */}
                <div className="flex items-center gap-3 flex-wrap text-[11px]" data-testid="vlz-presu-version">
                  <span className="rounded bg-[#0b3a4d] px-2 py-1 font-bold text-white">Versión contractual: {active.contract?.version || "V1"} — {active.contract?.version && active.contract.version !== "V1" ? "Vigente" : "Original"}</span>
                  <span className={`rounded px-2 py-1 font-semibold ${budgetEditable ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`} data-testid="vlz-presu-editable">
                    {budgetEditable ? "V1 en preparación · campos autorizados editables" : "🔒 V1 protegida (con valorizaciones)"}
                  </span>
                </div>
                <div className={`${CARD} overflow-auto`} style={{ maxHeight: 560 }} data-testid="vlz-presupuesto">
                  <div className="px-2 py-1 text-[10px] text-slate-500 bg-slate-50 border-b border-slate-200">
                    Estructura contractual: Capítulo → Subcapítulo → Partida · Parcial, subtotales y totales provienen del motor certificado (solo lectura). Scroll H/V interno.
                  </div>
                  <table className="min-w-max border-collapse text-[11px]">
                    <thead className={`sticky top-0 ${THEAD} z-10`}>
                      <tr>
                        <th className={`px-2 py-1 text-left sticky left-0 ${THEAD} z-20 min-w-[120px]`}>Item</th>
                        <th className="px-2 py-1 text-left min-w-[280px]">Descripción</th>
                        <th className="px-2 py-1 text-center min-w-[60px]">Und.</th>
                        <th className="px-2 py-1 text-right min-w-[100px]">Metrado</th>
                        <th className="px-2 py-1 text-right min-w-[110px]">Precio (S/)</th>
                        <th className="px-2 py-1 text-right min-w-[120px]">Parcial (S/)</th>
                      </tr>
                    </thead>
                    <tbody className="text-[#17263A]">
                      {budgetTree.filter((b) => !isHidden(b.code)).map((b) => {
                        const depth = b.level;
                        const grupo = b.group;
                        if (grupo) {
                          const collapsed = !!collapsedGroups[b.code];
                          return (
                            <tr key={`g-${b.code}`} className="border-t border-slate-200 bg-slate-100/80" data-testid={`vlz-presu-grupo-${b.code}`}>
                              <td className="px-2 py-1 font-mono font-bold sticky left-0 bg-slate-100 text-[#0b3a4d]" style={{ paddingLeft: 8 + depth * 14 }}>
                                <button onClick={() => setCollapsedGroups((s) => ({ ...s, [b.code]: !s[b.code] }))} data-testid={`vlz-presu-toggle-${b.code}`} className="inline-flex items-center gap-1">
                                  {collapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />} {b.code}
                                </button>
                              </td>
                              <td className="px-2 py-1 font-bold uppercase tracking-wide text-[#0b3a4d]">{b.description ? b.description : `${b.level === 0 ? "Capítulo" : "Subcapítulo"} ${b.code}`}</td>
                              <td colSpan={2}></td>
                              <td className="px-2 py-1 text-right text-[10px] uppercase text-slate-400">Subtotal</td>
                              <td className="px-2 py-1 text-right tabular-nums font-bold text-[#0b3a4d]" data-testid={`vlz-presu-subtotal-${b.code}`}>{n2(groupSubtotal(b))}</td>
                            </tr>
                          );
                        }
                        const inp = "w-full rounded border border-slate-300 bg-white px-1 py-0.5 text-[11px] text-[#17263A] focus:border-emerald-500 focus:outline-none";
                        return (
                          <tr key={b.line_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`vlz-presu-row-${b.line_id}`}>
                            <td className="px-2 py-1 font-mono text-slate-500 sticky left-0 bg-white" style={{ paddingLeft: 8 + depth * 14 }}>
                              {budgetEditable ? <input className={inp + " w-24"} value={budgetCell(b.line_id, "code", b.code)} data-testid={`vlz-presu-code-${b.line_id}`} onChange={(e) => setBudgetCell(b.line_id, "code", e.target.value)} /> : b.code}
                            </td>
                            <td className="px-2 py-1 max-w-[380px]">
                              {budgetEditable ? <input className={inp} value={budgetCell(b.line_id, "description", b.description)} data-testid={`vlz-presu-desc-${b.line_id}`} onChange={(e) => setBudgetCell(b.line_id, "description", e.target.value)} /> : <span className="truncate block" title={b.description}>{b.description}</span>}
                            </td>
                            <td className="px-2 py-1 text-center text-slate-500">
                              {budgetEditable ? <input className={inp + " w-14 text-center"} value={budgetCell(b.line_id, "unit", b.unit)} data-testid={`vlz-presu-unit-${b.line_id}`} onChange={(e) => setBudgetCell(b.line_id, "unit", e.target.value)} /> : b.unit}
                            </td>
                            <td className="px-2 py-1 text-right tabular-nums">
                              {budgetEditable ? <input type="number" step="any" className={inp + " w-24 text-right"} value={budgetCell(b.line_id, "m_c", b.m_c)} data-testid={`vlz-presu-metrado-${b.line_id}`} onChange={(e) => setBudgetCell(b.line_id, "m_c", e.target.value)} /> : n2(b.m_c)}
                            </td>
                            <td className="px-2 py-1 text-right tabular-nums text-slate-600">
                              {budgetEditable ? <input type="number" step="any" className={inp + " w-24 text-right"} value={budgetCell(b.line_id, "pu", b.pu)} data-testid={`vlz-presu-pu-${b.line_id}`} onChange={(e) => setBudgetCell(b.line_id, "pu", e.target.value)} /> : n2(b.pu)}
                            </td>
                            <td className="px-2 py-1 text-right tabular-nums font-medium" title="Parcial (motor certificado · no editable)" data-testid={`vlz-presu-parcial-${b.line_id}`}>{presMap[b.line_id] !== undefined ? n2(presMap[b.line_id]) : "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/* §23–§24 PIE DE PRESUPUESTO (debajo de la última partida, en Pantalla 01) */}
                <div className={`${CARD} p-3`} data-testid="vlz-pie-presupuesto">
                  <div className={`${secTitle} mb-2`}>Pie de Presupuesto</div>
                  {!pie ? <div className="text-[11px] text-slate-400">Cargando pie…</div> : (
                    <div className="ml-auto max-w-md space-y-1 text-[12px]">
                      <div className="flex justify-between"><span className="text-slate-500">Costo Directo</span><span className="tabular-nums font-medium" data-testid="vlz-pie-costo-directo">S/ {n2(pie.costo_directo)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Gastos Generales ({pct(pie.gg_rate)})</span><span className="tabular-nums">S/ {n2(pie.gastos_generales)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Utilidad ({pct(pie.util_rate)})</span><span className="tabular-nums">S/ {n2(pie.utilidad)}</span></div>
                      <div className="flex justify-between border-t border-slate-200 pt-1 font-bold text-[#0b3a4d]"><span>SUB TOTAL</span><span className="tabular-nums" data-testid="vlz-pie-subtotal">S/ {n2(pie.sub_total)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">IGV ({pct(pie.igv_rate)})</span><span className="tabular-nums">S/ {n2(pie.igv)}</span></div>
                      <div className="flex justify-between rounded bg-emerald-50 border border-emerald-500/40 px-2 py-1 mt-1 font-extrabold text-emerald-700"><span>MONTO CONTRACTUAL</span><span className="tabular-nums" data-testid="vlz-pie-monto-contractual">S/ {n2(pie.monto_contractual)}</span></div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* FÓRMULA POLINÓMICA */}
            {mod === "fp" && (
              proc?.fp ? (
                <div className={`${CARD} p-3`} data-testid="vlz-proc-fp">
                  <div className={`${secTitle} mb-1`}>Fórmula Polinómica · {proc.fp.iu_month} (Base {proc.fp.base_month})</div>
                  <div className="text-[11px] text-slate-600 mb-2 font-mono">{proc.fp.formula}</div>
                  <div className="overflow-auto rounded border border-slate-200" style={{ maxHeight: 480 }}>
                    <table className="w-full text-[10px] border-collapse">
                      <thead className={`sticky top-0 ${THEAD}`}><tr>
                        {["IU", "Descripción", "Coef.", "Incid.", "Io (base)", "Ir (actual)", "Ir/Io", "Aporte s/red.", "Aporte red."].map((hd) => <th key={hd} className="px-2 py-1 text-left">{hd}</th>)}
                      </tr></thead>
                      <tbody className="text-[#17263A]">
                        {proc.fp.monomials.map((m) => m.terms.map((t, i) => (
                          <tr key={m.code + t.index_code} className="border-t border-slate-100">
                            <td className="px-2 py-0.5 font-mono">{t.index_code}</td>
                            <td className="px-2 py-0.5">{t.desc}{i === 0 && m.terms.length > 1 ? ` (${m.symbol})` : ""}</td>
                            <td className="px-2 py-0.5">{i === 0 ? m.coef : ""}</td>
                            <td className="px-2 py-0.5">{t.incidencia}</td>
                            <td className="px-2 py-0.5 tabular-nums">{t.io}</td>
                            <td className="px-2 py-0.5 tabular-nums">{t.ir}</td>
                            <td className="px-2 py-0.5 tabular-nums">{t.ratio}</td>
                            <td className="px-2 py-0.5 tabular-nums text-slate-500">{t.aporte_sin_redondear}</td>
                            <td className="px-2 py-0.5 tabular-nums text-emerald-700">{t.aporte_redondeado_excel}</td>
                          </tr>
                        )))}
                      </tbody>
                      <tfoot className="bg-slate-100 font-bold text-[#17263A]"><tr>
                        <td className="px-2 py-1" colSpan={8}>K DEL PERIODO</td>
                        <td className="px-2 py-1 text-right text-emerald-700" data-testid="vlz-proc-k">{proc.fp.k_almacenado}</td>
                      </tr></tfoot>
                    </table>
                  </div>
                  <div className="mt-1 text-[10px] text-amber-600">Conciliación: {proc.fp.conciliacion} (K COREX {proc.fp.k_corex_por_monomio} = K Excel {proc.fp.k_excel_por_termino}).</div>
                </div>
              ) : <div className="text-[12px] text-slate-400 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> {conReaj ? "Cargando fórmula polinómica…" : "La Fórmula Polinómica aplica en modo 'Con reajustes'."}</div>
            )}

            {/* CÁLCULO DE REAJUSTE */}
            {mod === "reajuste" && (
              proc?.reajuste ? (
                <div className={`${CARD} p-4 max-w-md`} data-testid="vlz-proc-reajuste">
                  <div className={`${secTitle} mb-2`}>Cálculo de Reajuste · R = V × (K − 1)</div>
                  <div className="text-[12px] text-slate-600 font-mono mb-1">{proc.reajuste.formula}</div>
                  <div className="text-[13px] text-slate-700">{n2(proc.reajuste.V)} × ({proc.reajuste.K} − 1)</div>
                  <div className="mt-2 text-[22px] font-extrabold text-emerald-700 tabular-nums">S/ {n2(proc.reajuste.resultado)}</div>
                </div>
              ) : <div className="text-[12px] text-slate-400">{conReaj ? "Sin reajuste calculado para este periodo." : "El reajuste aplica en modo 'Con reajustes'."}</div>
            )}

            {/* REINTEGRO */}
            {mod === "reintegro" && (
              proc?.reintegro ? (
                <div className={`${CARD} p-4 max-w-lg`} data-testid="vlz-proc-reintegro">
                  <div className={`${secTitle} mb-2`}>Reintegro (origen Val.{String(proc.reintegro.origen_periodo).padStart(2, "0")} → Val.{String(proc.reintegro.reconocido_en).padStart(2, "0")})</div>
                  <div className="text-[11px] text-slate-600 font-mono mb-1">{proc.reintegro.formula}</div>
                  <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-600">
                    <div>K original: <b>{proc.reintegro.K_original}</b></div>
                    <div>K actualizado: <b>{proc.reintegro.K_actualizado}</b></div>
                    <div>Reajuste recalculado: {n2(proc.reintegro.reajuste_recalculado)}</div>
                    <div>Reajuste reconocido: {n2(proc.reintegro.reajuste_reconocido)}</div>
                  </div>
                  <div className="mt-2 text-[20px] font-extrabold text-emerald-700 tabular-nums">S/ {n2(proc.reintegro.resultado)}</div>
                </div>
              ) : <div className="text-[12px] text-slate-400">No hay reintegro reconocido en este periodo.</div>
            )}

            {/* AMORTIZACIÓN A.D. */}
            {mod === "amortizacion" && (
              proc?.amortizacion ? (
                <div className={`${CARD} p-3`} data-testid="vlz-proc-amortizacion">
                  <div className={`${secTitle} mb-1`}>Amortización del Adelanto Directo · A = AD × V / C</div>
                  <div className="text-[10px] text-slate-500 mb-2">AD = S/ {n2(proc.amortizacion.adelanto_directo)} ({proc.amortizacion.tasa_ad}) · C (contrato sin IGV) = S/ {n2(proc.amortizacion.contrato_sin_igv)}</div>
                  <div className="overflow-auto rounded border border-slate-200" style={{ maxHeight: 460 }}>
                    <table className="w-full text-[10px] border-collapse">
                      <thead className={`sticky top-0 ${THEAD}`}><tr>{["Val.", "V (sin IGV)", "Operación", "Amortización", "Acumulada", "Saldo A.D."].map((hd) => <th key={hd} className="px-2 py-1 text-left">{hd}</th>)}</tr></thead>
                      <tbody className="text-[#17263A]">{proc.amortizacion.rows.map((a) => (
                        <tr key={a.periodo} className="border-t border-slate-100">
                          <td className="px-2 py-0.5">{String(a.periodo).padStart(2, "0")}</td>
                          <td className="px-2 py-0.5 tabular-nums">{n2(a.V)}</td>
                          <td className="px-2 py-0.5 text-slate-500 tabular-nums">{n2(a.AD)} × {n2(a.V)} / {n2(a.C)}</td>
                          <td className="px-2 py-0.5 tabular-nums text-emerald-700">{n2(a.amortizacion)}</td>
                          <td className="px-2 py-0.5 tabular-nums">{n2(a.acumulada)}</td>
                          <td className="px-2 py-0.5 tabular-nums text-slate-500">{n2(a.saldo_por_amortizar)}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                </div>
              ) : <div className="text-[12px] text-slate-400">No hay adelanto directo registrado para amortizar.</div>
            )}

            {/* DEDUCCIÓN A.D. */}
            {mod === "deduccion" && (
              proc?.deduccion ? (
                <div className={`${CARD} p-4 max-w-lg`} data-testid="vlz-proc-deduccion">
                  <div className={`${secTitle} mb-2`}>Deducción A.D. (origen Val.{String(proc.deduccion.origen_periodo).padStart(2, "0")})</div>
                  <div className="text-[11px] text-slate-600 font-mono mb-1">{proc.deduccion.formula}</div>
                  <div className="text-[11px] text-slate-600">{proc.deduccion.operacion}</div>
                  <div className="mt-2 text-[20px] font-extrabold text-red-600 tabular-nums">S/ {n2(proc.deduccion.resultado)}</div>
                </div>
              ) : <div className="text-[12px] text-slate-400">No hay deducción de adelanto directo en este periodo.</div>
            )}

            {/* CONTROL DE EJECUCIÓN — Porcentaje de Ejecución / Financiero Programado */}
            {CONTROL_MODS.includes(mod) && (
              <div className="space-y-3">
                {mod === "control" && (
                <div className={`${CARD} overflow-auto`} style={{ maxHeight: 460 }} data-testid="vlz-control">
                  {!controlData ? (
                    <div className="p-4 text-[12px] text-slate-400">Cargando control de avance…</div>
                  ) : (
                    <table className="min-w-max border-collapse text-[11px]">
                      <thead className={`sticky top-0 ${THEAD} z-10`}>
                        <tr>
                          <th className={`px-2 py-1 text-left sticky left-0 ${THEAD}`}>Periodo</th>
                          <th className="px-2 py-1 text-right">Programado</th><th className="px-2 py-1 text-right">Ejecutado</th><th className="px-2 py-1 text-right">Dif. mensual</th>
                          <th className="px-2 py-1 text-right border-l border-white/20">Prog. acum.</th><th className="px-2 py-1 text-right">Ejec. acum.</th><th className="px-2 py-1 text-right">Dif. acum.</th>
                          <th className="px-2 py-1 text-right border-l border-white/20">% Prog.</th><th className="px-2 py-1 text-right">% Ejec.</th>
                        </tr>
                      </thead>
                      <tbody className="text-[#17263A]">
                        {(() => { let pa = 0, ea = 0; return controlData.rows.map((r) => {
                          pa += Number(r.programado || 0); ea += Number(r.ejecutado || 0);
                          return (
                            <tr key={r.period} className="border-t border-slate-100">
                              <td className="px-2 py-1 sticky left-0 bg-white">Val. {String(r.period).padStart(2, "0")}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{n2(r.programado)}</td>
                              <td className="px-2 py-1 text-right tabular-nums text-emerald-700">{n2(r.ejecutado)}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{n2(r.dif)}</td>
                              <td className="px-2 py-1 text-right tabular-nums border-l border-slate-100">{n2(pa)}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{n2(ea)}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{n2(ea - pa)}</td>
                              <td className="px-2 py-1 text-right tabular-nums border-l border-slate-100">{pct(r.pct_prog)}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{pct(r.pct_ejec)}</td>
                            </tr>
                          ); }); })()}
                      </tbody>
                    </table>
                  )}
                </div>
                )}
                {mod === "control-financiero" && controlData && (
                  <div className={`${CARD} p-3`} data-testid="vlz-programacion">
                    <div className={`${secTitle} mb-2`}>Programación por periodo (monto S/)</div>
                    <div className="flex flex-wrap gap-2 items-end">
                      {allPeriods.map((p) => (
                        <div key={p}>
                          <label className="block text-[10px] text-slate-400">Val. {String(p).padStart(2, "0")}</label>
                          <input type="number" step="any" data-testid={`vlz-prog-${p}`} defaultValue={active.programacion?.[String(p)] || ""}
                            onChange={(e) => setProg((s) => ({ ...s, [p]: e.target.value }))}
                            className="w-28 rounded border border-slate-300 bg-white px-2 py-1 text-right text-[11px] text-[#17263A]" />
                        </div>
                      ))}
                      <button onClick={saveProg} disabled={role === "AUDITOR"} data-testid="vlz-save-prog" className="rounded bg-emerald-600 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-emerald-500 disabled:opacity-50">Guardar programación</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ADELANTOS */}
            {mod === "adelantos" && (
              proc?.amortizacion ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl" data-testid="vlz-adelantos">
                  {[["Adelanto Directo (AD)", money(proc.amortizacion.adelanto_directo)], ["Tasa A.D.", proc.amortizacion.tasa_ad], ["Contrato (sin IGV)", money(proc.amortizacion.contrato_sin_igv)]].map(([l, v]) => (
                    <div key={l} className={`${CARD} p-3`}><div className="text-[10px] uppercase text-slate-400">{l}</div><div className="text-[16px] font-extrabold text-[#17263A] tabular-nums">{v}</div></div>
                  ))}
                  <div className={`${CARD} p-3 md:col-span-3 text-[11px] text-slate-500`}>
                    El Adelanto Directo se amortiza en cada valorización mediante A = AD × V / C (ver módulo Amortización A.D.). Los importes provienen del motor certificado.
                  </div>
                </div>
              ) : <div className="text-[12px] text-slate-400">Este proyecto no tiene adelanto directo registrado.</div>
            )}

            {/* ÍNDICES UNIFICADOS */}
            {mod === "indices" && (
              <div className="space-y-3" data-testid="vlz-indices">
                <div className={`${CARD} p-3`}>
                  <div className={`${secTitle} mb-2`}>Fórmula Polinómica · Cálculo de Factor K</div>
                  <button onClick={computeK} data-testid="vlz-compute-k" className="rounded bg-emerald-600 px-3 py-1.5 text-[12px] font-bold text-white hover:bg-emerald-500">Calcular K con índices actuales</button>
                  {iu.kResult && (
                    <div className="mt-2 text-[12px] text-[#17263A]"><span className="font-bold text-emerald-700">K = {iu.kResult.K}</span>
                      <span className="ml-3 text-slate-500">{iu.kResult.components.map((c) => `${c.code}:${c.component}`).join("  ·  ")}</span></div>
                  )}
                </div>
                <div className={`${CARD} p-3`} data-testid="vlz-regimen-panel">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className={secTitle}>Régimen / Áreas geográficas oficiales</span>
                    {areasInfo && <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600" data-testid="vlz-areas-count">{areasInfo.base} · {areasInfo.areas_count} áreas</span>}
                    {modoTecnicoCtl.puede_ver && (
                      <div className="ml-auto flex gap-1">
                        {role === "ADMINISTRADOR" && (
                          <button onClick={runIngesta} disabled={ingestaBusy} data-testid="vlz-ingesta-run" className="flex items-center gap-1 rounded border border-emerald-500/60 px-2 py-0.5 text-[11px] font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">
                            {ingestaBusy ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />} Sincronizar ahora
                          </button>
                        )}
                        <button onClick={loadPending} data-testid="vlz-pending-refresh" className={btnGhost}>Ver pendientes</button>
                      </div>
                    )}
                  </div>
                  {areasInfo && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 text-[10px]" data-testid="vlz-areas-map">
                      {Object.entries(areasInfo.areas).map(([a, deps]) => (
                        <div key={a} className="rounded border border-slate-200 bg-slate-50 px-2 py-1"><span className="font-bold text-emerald-700">Área {a}</span><span className="text-slate-500"> · {deps.join(", ")}</span></div>
                      ))}
                    </div>
                  )}
                  {areasInfo?.resolucion && <div className="mt-1 text-[10px] text-slate-400">Fuente: {areasInfo.resolucion}</div>}
                  {pendingIU && modoTecnicoCtl.puede_ver && (
                    <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2" data-testid="vlz-pending-panel">
                      <div className="text-[10px] font-bold uppercase text-amber-700 mb-1">Índices PENDIENTES DE REVISIÓN ({pendingIU.count})</div>
                      {pendingIU.count === 0 ? (
                        <div className="text-[10px] text-slate-500">No hay índices pendientes. La ingesta automática deja aquí toda detección para aprobación administrativa (nunca entra al motor sin aprobar).</div>
                      ) : (
                        <table className="w-full text-[10px]"><thead className="text-slate-500"><tr><th className="text-left">Código</th><th className="text-left">Área</th><th className="text-left">Mes/Año</th><th className="text-right">Valor</th><th className="text-left">Fuente</th></tr></thead>
                          <tbody>{pendingIU.items.slice(0, 40).map((it, i) => (
                            <tr key={i} className="border-t border-amber-200"><td>{it.code}</td><td>{it.area}</td><td>{it.month}/{it.year}</td><td className="text-right tabular-nums">{it.valor_operativo}</td><td className="text-slate-500">{it.fuente}</td></tr>
                          ))}</tbody></table>
                      )}
                    </div>
                  )}
                </div>
                <div className={`${CARD} p-3`}>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className={secTitle}>Base de Índices Unificados</span>
                    <select value={iu.regime} onChange={(e) => setIu((s) => ({ ...s, regime: e.target.value }))} data-testid="vlz-iu-regime" className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] text-[#17263A]">
                      <option value="1992">Régimen 1992 (6 áreas)</option><option value="2025">Régimen 2025 (13 áreas)</option>
                    </select>
                    <input value={iu.area} onChange={(e) => setIu((s) => ({ ...s, area: e.target.value }))} data-testid="vlz-iu-area" className="w-14 rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] text-[#17263A]" placeholder="Área" />
                    {role === "ADMINISTRADOR" && (
                      <div className="ml-auto flex gap-1">
                        <button onClick={updateIU} data-testid="vlz-iu-update" className={btnGhost}>Actualizar IU</button>
                        <button onClick={addIU} data-testid="vlz-iu-add" className={btnGhost}>Agregar IU</button>
                        <button onClick={approvePending} data-testid="vlz-iu-approve" className="rounded bg-emerald-600 px-2 py-1 text-[11px] font-bold text-white hover:bg-emerald-500">Aprobar pendientes</button>
                      </div>
                    )}
                  </div>
                  {iu.items.length === 0 ? (
                    <div className="text-[12px] text-slate-400">Sin índices para régimen {iu.regime} · área {iu.area}. Un administrador puede registrarlos y aprobarlos.</div>
                  ) : (
                    <table className="w-full text-[11px]">
                      <thead className="text-slate-500"><tr><th className="text-left">Código</th><th className="text-left">Mes/Año</th><th className="text-right">Oficial</th><th className="text-right">Operativo</th><th className="text-left">Estado</th></tr></thead>
                      <tbody className="text-[#17263A]">
                        {iu.items.map((it, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td>{it.code}</td><td>{it.month}/{it.year}</td>
                            <td className="text-right tabular-nums">{it.valor_oficial}</td><td className="text-right tabular-nums">{it.valor_operativo}</td>
                            <td className={it.status === "OFICIAL" ? "text-emerald-700" : "text-amber-600"}>{it.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {/* ADENDAS */}
            {mod === "adendas" && (
              <div className="space-y-3" data-testid="vlz-adendas">
                <div className={`${CARD} p-3`}>
                  <div className={`${secTitle} mb-2`}>Registrar Adenda · Presupuesto V2</div>
                  <textarea value={adenda.text} onChange={(e) => setAdenda((s) => ({ ...s, text: e.target.value }))} data-testid="vlz-adenda-text"
                    placeholder="Pega la estructura V2 desde Excel (Item, Descripción, Und., Metrado, Precio)…"
                    className="w-full h-24 rounded border border-slate-300 bg-white p-2 text-[11px] text-[#17263A]" />
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <label className="text-[11px] text-slate-500">Vigencia desde Val.</label>
                    <input type="number" value={adenda.from} onChange={(e) => setAdenda((s) => ({ ...s, from: Number(e.target.value) }))} data-testid="vlz-adenda-from" className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-[11px] text-[#17263A]" />
                    <button onClick={adendaCompare} data-testid="vlz-adenda-compare" className={btnGhost}>Comparar V1 vs V2</button>
                    <button onClick={adendaApply} disabled={adenda.blocked || role === "AUDITOR"} data-testid="vlz-adenda-apply" className="rounded bg-emerald-600 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-emerald-500 disabled:opacity-40">Aplicar Adenda</button>
                    {adenda.blocked && <span className="text-[11px] font-bold text-red-600">Bloqueado: inconsistencia contractual (§67)</span>}
                  </div>
                </div>
                {adenda.rows && (
                  <div className={`${CARD} p-3`}>
                    <table className="w-full text-[11px]">
                      <thead className="text-slate-500"><tr><th className="text-left">Código</th><th className="text-left">Clasificación</th><th className="text-right">M.C. V1</th><th className="text-right">M.C. V2</th><th className="text-right">Ejecutado</th></tr></thead>
                      <tbody className="text-[#17263A]">
                        {adenda.rows.map((r, i) => (
                          <tr key={i} className={`border-t border-slate-100 ${r.inconsistencia ? "bg-red-50" : ""}`}>
                            <td>{r.code}</td><td className="text-slate-600">{r.clasificacion}{r.inconsistencia ? " ⚠" : ""}</td>
                            <td className="text-right tabular-nums">{n2(r.m_c_v1)}</td><td className="text-right tabular-nums">{n2(r.m_c_v2)}</td><td className="text-right tabular-nums">{n2(r.ejecutado)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* AUDITORÍA */}
            {mod === "auditoria" && (
              <div className={`${CARD} overflow-auto`} style={{ maxHeight: 620 }} data-testid="vlz-auditoria">
                {!audit ? (<div className="p-4 text-[12px] text-slate-400 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Cargando auditoría…</div>)
                  : audit.length === 0 ? (<div className="p-4 text-[12px] text-slate-400">Sin eventos de auditoría registrados para este proyecto.</div>)
                    : (
                      <table className="min-w-max border-collapse text-[11px]">
                        <thead className={`sticky top-0 ${THEAD}`}><tr>{["Fecha", "Módulo", "Acción", "Entidad", "Motivo"].map((hd) => <th key={hd} className="px-2 py-1 text-left">{hd}</th>)}</tr></thead>
                        <tbody className="text-[#17263A]">
                          {audit.map((ev, i) => (
                            <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                              <td className="px-2 py-1 text-slate-500 whitespace-nowrap">{ev.ts ? String(ev.ts).replace("T", " ").slice(0, 19) : "—"}</td>
                              <td className="px-2 py-1">{ev.module || "—"}</td>
                              <td className="px-2 py-1 font-medium">{ev.action || "—"}</td>
                              <td className="px-2 py-1 font-mono text-slate-500 max-w-[220px] truncate" title={ev.entity}>{ev.entity || "—"}</td>
                              <td className="px-2 py-1 text-slate-500 max-w-[240px] truncate" title={ev.motivo}>{ev.motivo || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
              </div>
            )}

            {/* REPORTES */}
            {mod === "reportes" && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl" data-testid="vlz-reportes">
                <button onClick={() => exportFile("pdf")} className={`${CARD} p-4 text-left hover:border-emerald-400`}><Printer size={18} className="text-[#0b3a4d]" /><div className="mt-2 text-[13px] font-bold text-[#17263A]">Valorización PDF</div><div className="text-[11px] text-slate-500">Val.{String(period).padStart(2, "0")} · formato oficial</div></button>
                <button onClick={() => exportFile("auditable")} className={`${CARD} p-4 text-left hover:border-emerald-400`}><FileSpreadsheet size={18} className="text-emerald-700" /><div className="mt-2 text-[13px] font-bold text-[#17263A]">Excel auditable</div><div className="text-[11px] text-slate-500">Trazabilidad 1:1 vs motor certificado</div></button>
                <button onClick={() => exportFile("resumen")} className={`${CARD} p-4 text-left hover:border-emerald-400`}><FileBarChart size={18} className="text-[#0b3a4d]" /><div className="mt-2 text-[13px] font-bold text-[#17263A]">Resumen económico</div><div className="text-[11px] text-slate-500">Cadena económica del cierre</div></button>
              </div>
            )}

            {/* CONFIGURACIÓN */}
            {mod === "configuracion" && (
              <div className="space-y-3 max-w-2xl" data-testid="vlz-configuracion">
                <div className={`${CARD} p-3`}>
                  <div className={`${secTitle} mb-2`}>Modo de valorización</div>
                  <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
                    <button onClick={() => conReaj && toggleMode()} className={seg(!conReaj)}>Directa</button>
                    <button onClick={() => !conReaj && toggleMode()} className={seg(conReaj)}>Con reajustes</button>
                  </div>
                </div>
                {modoTecnicoCtl.puede_ver && (
                  <div className={`${CARD} p-3 flex items-center justify-between`}>
                    <div><div className={secTitle}>Modo Técnico</div><div className="text-[11px] text-slate-500">Muestra desgloses de ingeniería (FP, reajuste, reintegro…).</div></div>
                    <button onClick={toggleModoTecnico} disabled={!modoTecnicoCtl.puede_alternar} className={`rounded px-3 py-1.5 text-[12px] font-bold ${modoTecnico ? "bg-emerald-600 text-white" : "border border-slate-200 text-slate-600"} ${modoTecnicoCtl.puede_alternar ? "" : "opacity-70 cursor-default"}`}>{modoTecnico ? "ON" : "OFF"}</button>
                  </div>
                )}
                <div className={`${CARD} p-3 text-[11px] text-slate-500`}>Distribución de pantalla: <b className="text-[#17263A]">75 / 25</b> (Pantalla 01 workspace · Pantalla 02 resumen). Estable frente al zoom (80–200%).</div>
              </div>
            )}

            {/* USUARIOS */}
            {mod === "usuarios" && (
              <div className="space-y-3 max-w-2xl" data-testid="vlz-usuarios">
                <div className={`${CARD} p-3`}>
                  <div className={`${secTitle} mb-2`}>Rol de la sesión actual</div>
                  <span className="inline-flex items-center gap-2 rounded-full bg-[#0b3a4d] px-3 py-1 text-[12px] font-bold text-white">{role}</span>
                </div>
                <div className={`${CARD} p-3`}>
                  <div className={`${secTitle} mb-2`}>Permisos por rol</div>
                  <table className="w-full text-[11px]">
                    <thead className="text-slate-500"><tr><th className="text-left py-1">Rol</th><th className="text-left">Acceso</th></tr></thead>
                    <tbody className="text-[#17263A]">
                      <tr className="border-t border-slate-100"><td className="py-1 font-semibold">ADMINISTRADOR</td><td>Creación, edición, cierre, adendas, índices, modo técnico</td></tr>
                      <tr className="border-t border-slate-100"><td className="py-1 font-semibold">USUARIO</td><td>Edición de metrados en periodos abiertos, cálculo, exportación</td></tr>
                      <tr className="border-t border-slate-100"><td className="py-1 font-semibold">AUDITOR</td><td>Solo lectura de todo el expediente y trazabilidad</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>

          {/* PANTALLA 02 (25%) — Resumen Económico fijo arriba + contexto del módulo debajo */}
          <aside className="flex flex-col gap-3 overflow-y-auto min-w-0" style={{ flex: "1 1 0%" }} data-testid="vlz-pantalla-02">
            <div className={`${CARD} p-3`} data-testid="vlz-resumen-panel">
              <div className={`${secTitle} mb-2`}>Resumen económico</div>
              <ResumenEconomico vlz={vlz} />
            </div>
            {(() => { const ctx = renderAux(); return ctx ? (
              <div className={`${CARD} p-3`} data-testid="vlz-aux-panel">
                <div className={`${secTitle} mb-2`}>Contexto · {activeLabel}</div>
                {ctx}
              </div>
            ) : null; })()}
            <div className={`${CARD} p-3 text-[11px] text-slate-500`}>
              <div className="flex items-center gap-1 font-bold text-[#17263A] mb-1"><ShieldCheck size={13} color={GREEN} /> Motor certificado</div>
              Todos los importes se calculan en el backend con aritmética decimal (S/ 0.00 de tolerancia). El frontend nunca recalcula (§3).
            </div>
          </aside>
        </div>
      </div>

      {/* §1 · IMPORTAR PRESUPUESTO = selector de archivo (Excel/PDF) */}
      <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.pdf" onChange={onFileChosen} className="hidden" data-testid="vlz-import-file" />

      {importPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="vlz-import-preview">
          <div className="w-full max-w-3xl rounded-lg bg-white shadow-xl flex flex-col" style={{ maxHeight: "85vh" }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div className="text-[13px] font-bold text-[#0b3a4d]">Previsualización de importación · {importPreview.source} · <span className="font-normal text-slate-500">{importPreview.filename}</span></div>
              <button onClick={() => setImportPreview(null)} data-testid="vlz-import-cancel-x" className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <div className="px-4 py-2 flex items-center gap-2 text-[11px]" data-testid="vlz-import-resumen">
              <span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 font-semibold">RECONOCIDO {importPreview.validation?.resumen?.reconocido ?? 0}</span>
              <span className="rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 font-semibold">CON ADVERTENCIA {importPreview.validation?.resumen?.revisar ?? 0}</span>
              <span className="rounded-full bg-red-100 text-red-700 px-2 py-0.5 font-semibold">NO RECONOCIDO {importPreview.validation?.resumen?.error ?? 0}</span>
              <span className="ml-auto text-slate-400">{importPreview.partidas?.length || 0} filas</span>
            </div>
            <div className="overflow-auto px-4" style={{ flex: 1 }}>
              <table className="w-full text-[11px] border-collapse">
                <thead className="sticky top-0 bg-slate-100 text-slate-600"><tr>
                  <th className="px-2 py-1 text-left">Item</th><th className="px-2 py-1 text-left">Descripción</th><th className="px-2 py-1 text-center">Und.</th>
                  <th className="px-2 py-1 text-right">Metrado</th><th className="px-2 py-1 text-right">P.U.</th><th className="px-2 py-1 text-left">Estado</th>
                </tr></thead>
                <tbody className="text-[#17263A]">
                  {(importPreview.partidas || []).map((p, i) => {
                    const row = importPreview.validation?.rows?.[i] || {};
                    const st = row.status === "error" ? ["NO RECONOCIDO", "bg-red-50 text-red-700"] : row.status === "warn" ? ["CON ADVERTENCIA", "bg-amber-50 text-amber-700"] : ["RECONOCIDO", "bg-emerald-50 text-emerald-700"];
                    return (
                      <tr key={i} className="border-t border-slate-100" data-testid={`vlz-import-row-${i}`}>
                        <td className="px-2 py-1 font-mono text-slate-500">{p.code}</td>
                        <td className="px-2 py-1 max-w-[260px] truncate" title={p.description}>{p.is_partida === false ? <b>{p.description}</b> : p.description}</td>
                        <td className="px-2 py-1 text-center text-slate-500">{p.unit}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{p.m_c != null ? n2(p.m_c) : ""}</td>
                        <td className="px-2 py-1 text-right tabular-nums text-slate-600">{p.pu != null ? n2(p.pu) : ""}</td>
                        <td className="px-2 py-1"><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${st[1]}`}>{st[0]}</span>{row.flags?.length ? <span className="ml-1 text-[10px] text-slate-400">{row.flags.join(", ")}</span> : null}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
              <span className="text-[11px] text-slate-500">{!importPreview.validation?.ok ? "Hay filas NO RECONOCIDAS: corrígelas en el archivo o revisa el mapeo antes de confirmar." : "Estructura válida. Al confirmar se incorpora al Presupuesto Contractual."}</span>
              <div className="flex gap-2">
                <button onClick={() => setImportPreview(null)} data-testid="vlz-import-cancel" className={btnGhost}>Cancelar</button>
                <button onClick={confirmFileImport} disabled={busy || !importPreview.validation?.ok} data-testid="vlz-import-confirm" className="rounded-md bg-emerald-600 px-4 py-1.5 text-[12px] font-bold text-white hover:bg-emerald-500 disabled:opacity-40">Confirmar importación</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
